import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { pingTask } from '../../../util/apiService';
import { 
  closeCopyReplace, 
  setTargetDocument, 
  setPreviewMode, 
  setComment,
  setOriginalData,
  setNewData,
  setLoading
} from '../../../redux/previewSlice';
import { getRevAllNodes, updateCurrentSnapshot } from '../../../redux/procedureRevisionSlice';
import { copyProcedure, copyElement, copyNodeVersionHistory, copyVersionProcedureTreeNode } from '../../../redux/sectionSlice';
import { getAuditTrailData, extractVersionStructure } from '../../../util/getAuditTrailData';
import { showSnackbar } from '../../../redux/snackbarSlice';

import SelectRevisionTable from '../assets/components/SelectRevision/SelectRevisionTable';
import VersionHistoryCopyAppBar from '../../../common/VersionHistoryCopyAppBar';
import CopyReplacePreview from './components/CopyReplacePreview';
import CopyReplaceConfirm from './components/CopyReplaceConfirm';
import PastePositionSelector from './components/PastePositionSelector';
import Loader from '../../../common/Loader';
import { generateNodeIndexing } from '../../../hooks/useNodeIndexing';
import { buildNodeIndexingForSectionCopy, prepareCopyNewContent } from '../Move/utils/sectionMoveCopyUtils';
import { buildNodeIndexingForStepCopy, prepareStepMoveNewContent } from '../Move/utils/stepMoveCopyUtils';
import { buildNodeIndexingForSubstepCopy, prepareSubstepMoveNewContent } from '../Move/utils/substepMoveCopyUtils';
import { getChildNodeDisplayNumber, formatChildTypeLabel } from '../Move/utils/childNodeMoveCopyUtils';
import { getRevAndTraining, handleRedirectToCopiedNode, prepareCurrentSnapshot, uploadJsonToS3 } from '../../../util/AppUtils';
import { fetchtAllTasks,  openTaskPopup, setCopiedAndRedirect } from '../../../redux/taskSlice';

const CHILD_NODE_ID_FIELDS = [
  'attachment_tree_node_id',
  'video_tree_node_id',
  'rich_text_node_id',
  'canvas_tree_node_id',
  'form_tree_node_id',
];

const getSiblingOrderNumber = (node) => {
  if (!node) return 0;
  const order =
    node.sibling_order ??
    node.procedure_tree?.sibling_order ??
    node.detail?.procedure_tree?.sibling_order ??
    '0';
  const parsed = parseInt(order, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortBySiblingOrder = (items = []) =>
  [...items].sort((a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b));

const getChildNodeId = (child) => {
  if (!child) return null;
  if (child.tree_node_id) return child.tree_node_id;
  if (child.node_id) return child.node_id;
  if (child.sub_step_tree_node_id) return child.sub_step_tree_node_id;
  const detail = child.detail || {};
  for (const field of CHILD_NODE_ID_FIELDS) {
    if (detail[field]) {
      return detail[field];
    }
  }
  return null;
};

const resolveNodeNameWithFallback = (indexMap, id, node, defaultLabel) => {
  if (id && indexMap && indexMap[id]) {
    return indexMap[id];
  }
  if (node) {
    const candidateFields = [
      'title',
      'name',
      'label',
      'display_name',
      'section_name',
      'step_name',
      'substep_name',
    ];
    for (const field of candidateFields) {
      if (node[field]) {
        return node[field];
      }
    }
    if (node.detail) {
      for (const field of candidateFields) {
        if (node.detail[field]) {
          return node.detail[field];
        }
      }
    }
    if (node.type) {
      const type = node.type.toString();
      return type.charAt(0).toUpperCase() + type.slice(1);
    }
  }
  return defaultLabel;
};

const collectHierarchyNodesForCopyIndexing = (sections = [], indexMap = {}) => {
  const nodes = [];

  const addNode = (entry) => {
    const { id } = entry;
    if (!id) return;
    if (nodes.some((existing) => existing.id === id)) return;
    nodes.push(entry);
  };

  const sortedSections = sortBySiblingOrder(sections);
  sortedSections.forEach((section) => {
    const sectionId = section?.tree_node_id || section?.section_id_ref;
    const sectionLabel = resolveNodeNameWithFallback(indexMap, sectionId, section, 'Section');
    addNode({ id: sectionId, label: sectionLabel, type: 'section' });

    const sectionChildren = sortBySiblingOrder(section?.children || []);
    sectionChildren.forEach((child) => {
      const childId = getChildNodeId(child);
      const childLabel = resolveNodeNameWithFallback(indexMap, childId, child, 'Element');
      addNode({
        id: childId,
        label: childLabel,
        type: 'sectionChild',
        parentSectionLabel: sectionLabel,
      });
    });

    const sortedSteps = sortBySiblingOrder(section?.steps || []);
    sortedSteps.forEach((step) => {
      const stepId = step?.tree_node_id || step?.step_id_ref;
      const stepLabel = resolveNodeNameWithFallback(indexMap, stepId, step, 'Step');
      addNode({
        id: stepId,
        label: stepLabel,
        type: 'step',
        parentSectionLabel: sectionLabel,
      });

      const stepChildren = sortBySiblingOrder(step?.children || []);
      stepChildren.forEach((child) => {
        const childId = getChildNodeId(child);
        const childLabel = resolveNodeNameWithFallback(indexMap, childId, child, 'Element');
        addNode({
          id: childId,
          label: childLabel,
          type: 'stepChild',
          parentSectionLabel: sectionLabel,
          parentStepLabel: stepLabel,
        });
      });

      const sortedSubsteps = sortBySiblingOrder(step?.substeps || []);
      sortedSubsteps.forEach((substep) => {
        const substepId = substep?.tree_node_id || substep?.substep_id_ref;
        const substepLabel = resolveNodeNameWithFallback(indexMap, substepId, substep, 'Substep');
        addNode({
          id: substepId,
          label: substepLabel,
          type: 'substep',
          parentSectionLabel: sectionLabel,
          parentStepLabel: stepLabel,
        });

        const substepChildren = sortBySiblingOrder(substep?.children || []);
        substepChildren.forEach((child) => {
          const childId = getChildNodeId(child);
          const childLabel = resolveNodeNameWithFallback(indexMap, childId, child, 'Element');
          addNode({
            id: childId,
            label: childLabel,
            type: 'substepChild',
            parentSectionLabel: sectionLabel,
            parentStepLabel: stepLabel,
            parentSubstepLabel: substepLabel,
          });
        });
      });
    });
  });

  return nodes;
};

// Helper to find a node in sourceNodes by ID
const findNodeInSource = (sourceNodes, nodeId, type) => {
  for (const section of sourceNodes) {
    if ((section.tree_node_id === nodeId || section.section_id_ref === nodeId) && type === 'section') {
      return section;
    }
    
    // Check section children
    if (section.children) {
      for (const child of section.children) {
        const childId = getChildNodeId(child);
        if (childId === nodeId && type === 'sectionChild') {
          return child;
        }
      }
    }
    
    // Check steps
    if (section.steps) {
      for (const step of section.steps) {
        if ((step.tree_node_id === nodeId || step.step_id_ref === nodeId) && type === 'step') {
          return step;
        }
        
        // Check step children
        if (step.children) {
          for (const child of step.children) {
            const childId = getChildNodeId(child);
            if (childId === nodeId && type === 'stepChild') {
              return child;
            }
          }
        }
        
        // Check substeps
        if (step.substeps) {
          for (const substep of step.substeps) {
            if ((substep.tree_node_id === nodeId || substep.substep_id_ref === nodeId) && type === 'substep') {
              return substep;
            }
            
            // Check substep children
            if (substep.children) {
              for (const child of substep.children) {
                const childId = getChildNodeId(child);
                if (childId === nodeId && type === 'substepChild') {
                  return child;
                }
              }
            }
          }
        }
      }
    }
  }
  return null;
};

// Helper to strip out children that haven't been added yet (for incremental building)
const stripUnaddedChildren = (node, type) => {
  const stripped = JSON.parse(JSON.stringify(node));
  
  // For sections: remove steps, substeps, and children (they'll be added in separate operations)
  if (type === 'section') {
    delete stripped.steps;
    delete stripped.children;
  }
  
  // For steps: remove substeps and children (they'll be added in separate operations)
  if (type === 'step') {
    delete stripped.substeps;
    delete stripped.children;
  }
  
  // For substeps: remove children (they'll be added in separate operations)
  if (type === 'substep') {
    delete stripped.children;
  }
  
  // For children nodes: they don't have nested children, so no stripping needed
  
  return stripped;
};

// Helper to add a node to the current structure incrementally
const addNodeToStructure = (currentStructure, nodeToAdd, hierarchyNode, sourceNodes) => {
  const clonedStructure = JSON.parse(JSON.stringify(currentStructure));
  const { type, id } = hierarchyNode;
  
  const nodeData = findNodeInSource(sourceNodes, id, type);
  if (!nodeData) return clonedStructure;
  
  // Clone the node but strip out children that haven't been added yet
  const clonedNode = stripUnaddedChildren(nodeData, type);
  
  if (type === 'section') {
    // Add section at the end (without steps/children - they'll be added separately)
    clonedStructure.push(clonedNode);
    // Normalize sibling orders
    clonedStructure.forEach((section, index) => {
      section.sibling_order = index.toString();
    });
  } else if (type === 'sectionChild') {
    // Find parent section in currentStructure (look for the section that was added from source)
    const parentSection = clonedStructure.find(sec => 
      sec.tree_node_id === hierarchyNode.parentSectionSourceId || 
      sec.section_id_ref === hierarchyNode.parentSectionSourceId ||
      sec.tree_node_id === hierarchyNode.parentSectionId || 
      sec.section_id_ref === hierarchyNode.parentSectionId
    );
    if (parentSection) {
      if (!parentSection.children) parentSection.children = [];
      parentSection.children.push(clonedNode);
      // Normalize sibling orders
      parentSection.children.forEach((child, index) => {
        child.sibling_order = index.toString();
      });
    }
  } else if (type === 'step') {
    // Find parent section in currentStructure
    const parentSection = clonedStructure.find(sec => 
      sec.tree_node_id === hierarchyNode.parentSectionSourceId || 
      sec.section_id_ref === hierarchyNode.parentSectionSourceId ||
      sec.tree_node_id === hierarchyNode.parentSectionId || 
      sec.section_id_ref === hierarchyNode.parentSectionId
    );
    if (parentSection) {
      if (!parentSection.steps) parentSection.steps = [];
      parentSection.steps.push(clonedNode);
      // Normalize sibling orders
      parentSection.steps.forEach((step, index) => {
        step.sibling_order = index.toString();
      });
    }
  } else if (type === 'stepChild') {
    // Find parent step in currentStructure
    for (const section of clonedStructure) {
      if (section.steps) {
        const parentStep = section.steps.find(st => 
          st.tree_node_id === hierarchyNode.parentStepSourceId || 
          st.step_id_ref === hierarchyNode.parentStepSourceId ||
          st.tree_node_id === hierarchyNode.parentStepId || 
          st.step_id_ref === hierarchyNode.parentStepId
        );
        if (parentStep) {
          if (!parentStep.children) parentStep.children = [];
          parentStep.children.push(clonedNode);
          // Normalize sibling orders
          parentStep.children.forEach((child, index) => {
            child.sibling_order = index.toString();
          });
          break;
        }
      }
    }
  } else if (type === 'substep') {
    // Find parent step in currentStructure
    for (const section of clonedStructure) {
      if (section.steps) {
        const parentStep = section.steps.find(st => 
          st.tree_node_id === hierarchyNode.parentStepSourceId || 
          st.step_id_ref === hierarchyNode.parentStepSourceId ||
          st.tree_node_id === hierarchyNode.parentStepId || 
          st.step_id_ref === hierarchyNode.parentStepId
        );
        if (parentStep) {
          if (!parentStep.substeps) parentStep.substeps = [];
          parentStep.substeps.push(clonedNode);
          // Normalize sibling orders
          parentStep.substeps.forEach((substep, index) => {
            substep.sibling_order = index.toString();
          });
          break;
        }
      }
    }
  } else if (type === 'substepChild') {
    // Find parent substep in currentStructure
    for (const section of clonedStructure) {
      if (section.steps) {
        for (const step of section.steps) {
          if (step.substeps) {
            const parentSubstep = step.substeps.find(ss => 
              ss.tree_node_id === hierarchyNode.parentSubstepSourceId || 
              ss.substep_id_ref === hierarchyNode.parentSubstepSourceId ||
              ss.tree_node_id === hierarchyNode.parentSubstepId || 
              ss.substep_id_ref === hierarchyNode.parentSubstepId
            );
            if (parentSubstep) {
              if (!parentSubstep.children) parentSubstep.children = [];
              parentSubstep.children.push(clonedNode);
              // Normalize sibling orders
              parentSubstep.children.forEach((child, index) => {
                child.sibling_order = index.toString();
              });
              break;
            }
          }
        }
      }
    }
  }
  
  return clonedStructure;
};

const buildCopyReplaceNodeIndexing = ({
  sourceNodes = [],
  targetNodes = [],
  sourceDocument,
  targetDocument,
  startswith=0,
  actionToShow=''
}) => {
  if (!Array.isArray(sourceNodes) || sourceNodes.length === 0) {
    return {};
  }

  const indexMap = generateNodeIndexing(sourceNodes);
  const hierarchyNodes = collectHierarchyNodesForCopyIndexing(sourceNodes, indexMap);
  if (hierarchyNodes.length === 0) {
    return {};
  }

  const actionPrefix = actionToShow;
  const totalOperations = hierarchyNodes.length;
  const nodeIndexing = {};

  let currentStructure = [];
  const baseTargetSnapshot = structuredClone(
    Array.isArray(targetNodes) ? targetNodes : []
  ) || [];
  let previousSnapshot = null;

  hierarchyNodes.forEach((hierarchyNode, index) => {
    const { id, label, type, parentSectionLabel, parentStepLabel, parentSubstepLabel } = hierarchyNode;
    
    // Store parent IDs for finding parent nodes in sourceNodes
    // These will be used to find the corresponding nodes in currentStructure
    if (type === 'sectionChild' || type === 'step' || type === 'substep') {
      // Find parent section ID from sourceNodes
      for (const section of sourceNodes) {
        if (type === 'sectionChild' && section.children) {
          const child = section.children.find(c => getChildNodeId(c) === id);
          if (child) {
            hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
            hierarchyNode.parentSectionSourceId = section.tree_node_id || section.section_id_ref;
            break;
          }
        } else if (type === 'step' && section.steps) {
          const step = section.steps.find(s => (s.tree_node_id === id || s.step_id_ref === id));
          if (step) {
            hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
            hierarchyNode.parentSectionSourceId = section.tree_node_id || section.section_id_ref;
            break;
          }
        } else if (type === 'substep' && section.steps) {
          for (const step of section.steps) {
            if (step.substeps) {
              const substep = step.substeps.find(ss => (ss.tree_node_id === id || ss.substep_id_ref === id));
              if (substep) {
                hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
                hierarchyNode.parentStepId = step.tree_node_id || step.step_id_ref;
                hierarchyNode.parentSectionSourceId = section.tree_node_id || section.section_id_ref;
                hierarchyNode.parentStepSourceId = step.tree_node_id || step.step_id_ref;
                break;
              }
            }
          }
        }
      }
    }
    
    if (type === 'stepChild' || type === 'substepChild') {
      // Find parent step/substep IDs from sourceNodes
      for (const section of sourceNodes) {
        if (section.steps) {
          for (const step of section.steps) {
            if (type === 'stepChild' && step.children) {
              const child = step.children.find(c => getChildNodeId(c) === id);
              if (child) {
                hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
                hierarchyNode.parentStepId = step.tree_node_id || step.step_id_ref;
                hierarchyNode.parentSectionSourceId = section.tree_node_id || section.section_id_ref;
                hierarchyNode.parentStepSourceId = step.tree_node_id || step.step_id_ref;
                break;
              }
            } else if (type === 'substepChild' && step.substeps) {
              for (const substep of step.substeps) {
                if (substep.children) {
                  const child = substep.children.find(c => getChildNodeId(c) === id);
                  if (child) {
                    hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
                    hierarchyNode.parentStepId = step.tree_node_id || step.step_id_ref;
                    hierarchyNode.parentSubstepId = substep.tree_node_id || substep.substep_id_ref;
                    hierarchyNode.parentSectionSourceId = section.tree_node_id || section.section_id_ref;
                    hierarchyNode.parentStepSourceId = step.tree_node_id || step.step_id_ref;
                    hierarchyNode.parentSubstepSourceId = substep.tree_node_id || substep.substep_id_ref;
                    break;
                  }
                }
              }
            }
          }
        }
      }
    }
    
    let destinationDescription = '';
    const targetDescriptor = `${targetDocument?.partNumber || ''}, Rev ${targetDocument?.revision || ''}`;

    switch (type) {
      case 'section':
        destinationDescription = `${targetDescriptor}`;
        break;
      case 'step':
        destinationDescription = `${parentSectionLabel || 'the target section'} of ${targetDescriptor}`;
        break;
      case 'sectionChild':
        destinationDescription = `${parentSectionLabel || 'the target section'} of ${targetDescriptor}`;
        break;
      case 'stepChild':
        destinationDescription = `${parentStepLabel || 'the target step'} of ${targetDescriptor}`;
        break;
      case 'substep':
        destinationDescription = `${parentStepLabel || 'the target step'} of ${targetDescriptor}`;
        break;
      case 'substepChild':
        destinationDescription = `${parentSubstepLabel || 'the target substep'} of ${targetDescriptor}`;
        break;
      default:
        destinationDescription = targetDescriptor;
        break;
    }

    const title = `${actionPrefix} System Performs Operation ${index + 1 + startswith} of ${totalOperations+startswith}, Copy ${label} into ${destinationDescription}.`;
    
    const originalContent = previousSnapshot
      ? JSON.parse(JSON.stringify(previousSnapshot))
      : JSON.parse(JSON.stringify(baseTargetSnapshot));
    
    // Add the node to structure
    currentStructure = addNodeToStructure(currentStructure, null, hierarchyNode, sourceNodes);
    
    // Capture newContent (state after adding this node)
    const newContent = structuredClone(currentStructure) || [];
    
    // Store in node_indexing with new structure
    nodeIndexing[id] = {
      title,
      originalContent: originalContent || [],
      newContent: JSON.parse(JSON.stringify(newContent)),
    };
    
    previousSnapshot = JSON.parse(JSON.stringify(newContent));
  });

  return nodeIndexing;
};

/**
 * CopyReplace Component
 * Main component for copy & replace functionality
 * Manages the flow: Select Target → Preview → Confirm
 */
const CopyReplace = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { 
    sourceDocument, 
    sourceNode,
    copyMode,
    pastePosition,
    targetDocument, 
    previewMode, 
    comment,
    originalData,
    newData,
    isLoading,
    isFromMovePreview,
    movePreviewSourceData
  } = useSelector(state => state.preview);
 
  const authUser = useSelector((state) => state.auth.user);
  
  const [step, setStep] = useState('select'); // 'select' | 'pastePosition' | 'preview' | 'confirm'
  const [error, setError] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  
  // Start ping interval for source document
  useEffect(() => {
    if (!sourceDocument?.revisionId || !authUser) return;
    
    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: sourceDocument.revisionId,
          user_id: authUser.id
        };
        await pingTask(pingData);
      } catch (error) {
        console.error('Ping failed for source document in CopyReplace:', error);
      }
    }, 60000); // 60 seconds = 1 minute
    
    return () => {
      clearInterval(interval);
    };
  }, [sourceDocument?.revisionId, authUser]);
  
  // Start ping interval for target document (if different from source)
  useEffect(() => {
    if (!targetDocument?.revisionId || !authUser) return;
    // Only ping target if it's different from source
    if (targetDocument.revisionId === sourceDocument?.revisionId) return;
    
    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: targetDocument.revisionId,
          user_id: authUser.id
        };
        await pingTask(pingData);
      } catch (error) {
        console.error('Ping failed for target document in CopyReplace:', error);
      }
    }, 60000); // 60 seconds = 1 minute
    
    return () => {
      clearInterval(interval);
    };
  }, [targetDocument?.revisionId, sourceDocument?.revisionId, authUser]);

  // Handle document selection from SelectRevisionTable
  const handleRevisionSelect = async ({ group, revision }) => {
    try {
      setError(null);
      dispatch(setLoading(true));
      
      const targetDoc = {
        partId: group.partId,
        revisionId: revision.id,
        partNumber: group.partNumber,
        revision: revision.label,
        type: group.type,
        description: group.description,
        status: group.status || 'Not Submitted',
        tree_node_id:revision?.full?.tree_node_details?.tree_node_id,
        parent_based_version: revision?.full?.based_on_version_ref,
      };
      
      dispatch(setTargetDocument(targetDoc));
      
      // Fetch original data for the target document
      const originalResponse = await dispatch(getRevAllNodes({ 
        partId: targetDoc.partId, 
        revisionId: targetDoc.revisionId 
      })).unwrap();
      
      dispatch(setOriginalData(originalResponse));
      
      // Fetch source data to compute new structure
      const sourceResponse = await dispatch(getRevAllNodes({ 
        partId: sourceDocument.partId, 
        revisionId: sourceDocument.revisionId 
      })).unwrap();
      
      // For node copy, go to paste position selector; for document copy, go to preview
      if (copyMode === 'node') {
        setStep('pastePosition');
      } else {
        // Compute new data (replace target with source structure)
        dispatch(setNewData(sourceResponse));
        setStep('preview');
      }
    } catch (err) {
      console.error('Error fetching document data:', err);
      setError('Failed to load document data. Please try again.');
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Handle preview mode toggle
  const handlePreviewModeChange = (mode) => {
    dispatch(setPreviewMode(mode));
  };

  // Handle comment change
  const handleCommentChange = (newComment) => {
    dispatch(setComment(newComment));
  };

  // Handle position selection from PastePositionSelector
  const handlePositionSelected = async (position, targetType, targetId, targetIndex) => {
    try {
      dispatch(setLoading(true));
      
      // Fetch source document to get the section to copy
      // If isFromMovePreview is true, try using movePreviewSourceData first
      let sourceResponse = null;
      if (isFromMovePreview && movePreviewSourceData && Array.isArray(movePreviewSourceData) && movePreviewSourceData.length > 0) {
        sourceResponse = movePreviewSourceData;
      } else {
        sourceResponse = await dispatch(getRevAllNodes({ 
          partId: sourceDocument.partId, 
          revisionId: sourceDocument.revisionId 
        })).unwrap();
      }
      
      // Prepare working copy of target data and sorted sections
      let newTargetData = JSON.parse(JSON.stringify(originalData || []));
      const sortedSections = [...newTargetData].sort((a, b) => {
        const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
        const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
        return orderA - orderB;
      });

      // Find the source node (section or step or substep)
      let sourceSection = null;
      let sourceStep = null;
      let sourceSubstep = null;
      
      if (sourceNode.nodeType === 'section') {
        // For section copy: find the section directly
        sourceSection = sourceResponse.find(
          section => section.tree_node_id === sourceNode.nodeId || section.section_id_ref === sourceNode.nodeId
        );
        
        if (!sourceSection) {
          if (!isFromMovePreview) {
            console.error('Source section not found');
            dispatch(showSnackbar({ message: 'Source section not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          } else {
            // Bypass check - create minimal placeholder structure
            console.warn('Source section not found, creating placeholder for MovePreview');
            sourceSection = {
              tree_node_id: sourceNode.nodeId,
              section_id_ref: sourceNode.nodeId,
              title: sourceNode.nodeTitle || 'Section',
              steps: [],
              sibling_order: '0'
            };
          }
        }
      } else if (sourceNode.nodeType === 'step') {
        // For step copy: find the section containing the step, then find the step
        sourceSection = sourceResponse.find(
          section => section.tree_node_id === sourceNode.sectionId || section.section_id_ref === sourceNode.sectionId
        );
        
        if (!sourceSection) {
          if (!isFromMovePreview) {
            console.error('Source section not found for step');
            dispatch(showSnackbar({ message: 'Source section not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          } else {
            // Bypass check - create minimal placeholder structure
            console.warn('Source section not found, creating placeholder for MovePreview');
            sourceSection = {
              tree_node_id: sourceNode.sectionId,
              section_id_ref: sourceNode.sectionId,
              title: 'Section',
              steps: [],
              sibling_order: '0'
            };
          }
        }
        
        sourceStep = sourceSection.steps?.find(
          step => step.tree_node_id === sourceNode.nodeId || step.step_id_ref === sourceNode.nodeId
        );
        
        if (!sourceStep) {
          if (!isFromMovePreview) {
            console.error('Source step not found');
            dispatch(showSnackbar({ message: 'Source step not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          } else {
            // Bypass check - create minimal placeholder structure
            console.warn('Source step not found, creating placeholder for MovePreview');
            sourceStep = {
              tree_node_id: sourceNode.nodeId,
              step_id_ref: sourceNode.nodeId,
              title: sourceNode.nodeTitle || 'Step',
              substeps: [],
              children: [],
              sibling_order: '0'
            };
            if (!sourceSection.steps) {
              sourceSection.steps = [];
            }
            sourceSection.steps.push(sourceStep);
          }
        }
      } else if (sourceNode.nodeType === 'substep') {
        // For substep copy: find section, step, then substep
        sourceSection = sourceResponse.find(
          section => section.tree_node_id === sourceNode.sectionId || section.section_id_ref === sourceNode.sectionId
        );
        if (!sourceSection) {
          if (!isFromMovePreview) {
            console.error('Source section not found for substep');
            dispatch(showSnackbar({ message: 'Source section not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          } else {
            // Bypass check - create minimal placeholder structure
            console.warn('Source section not found, creating placeholder for MovePreview');
            sourceSection = {
              tree_node_id: sourceNode.sectionId,
              section_id_ref: sourceNode.sectionId,
              title: 'Section',
              steps: [],
              sibling_order: '0'
            };
          }
        }
        sourceStep = sourceSection.steps?.find(
          step => step.tree_node_id === sourceNode.stepId || step.step_id_ref === sourceNode.stepId
        );
        if (!sourceStep) {
          if (!isFromMovePreview) {
            console.error('Source step not found for substep');
            dispatch(showSnackbar({ message: 'Source step not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          } else {
            // Bypass check - create minimal placeholder structure
            console.warn('Source step not found, creating placeholder for MovePreview');
            sourceStep = {
              tree_node_id: sourceNode.stepId,
              step_id_ref: sourceNode.stepId,
              title: 'Step',
              substeps: [],
              children: [],
              sibling_order: '0'
            };
            if (!sourceSection.steps) {
              sourceSection.steps = [];
            }
            sourceSection.steps.push(sourceStep);
          }
        }
        sourceSubstep = sourceStep.substeps?.find(
          ss => ss.tree_node_id === sourceNode.nodeId || ss.substep_id_ref === sourceNode.nodeId
        );
        if (!sourceSubstep) {
          if (!isFromMovePreview) {
            console.error('Source substep not found');
            dispatch(showSnackbar({ message: 'Source substep not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          } else {
            // Bypass check - create minimal placeholder structure
            console.warn('Source substep not found, creating placeholder for MovePreview');
            sourceSubstep = {
              tree_node_id: sourceNode.nodeId,
              substep_id_ref: sourceNode.nodeId,
              title: sourceNode.nodeTitle || 'Substep',
              children: [],
              sibling_order: '0'
            };
            if (!sourceStep.substeps) {
              sourceStep.substeps = [];
            }
            sourceStep.substeps.push(sourceSubstep);
          }
        }
      }
      // Child node copy (Attachment, Video, Rich Text, Canvas, Form)
      else if ([ 'attachment','video','rich-text','form','canva','canvas' ].includes(sourceNode.nodeType)) {
        // Resolve source child from source tree
        sourceSection = sourceResponse.find(sec => sec.tree_node_id === sourceNode.sectionId || sec.section_id_ref === sourceNode.sectionId);
        if (!sourceSection) {
          if (!isFromMovePreview) {
            dispatch(showSnackbar({ message: 'Source section not found for child', type: 'error' }));
            dispatch(setLoading(false));
            return;
          } else {
            // Bypass check - create minimal placeholder structure
            console.warn('Source section not found, creating placeholder for MovePreview');
            sourceSection = {
              tree_node_id: sourceNode.sectionId,
              section_id_ref: sourceNode.sectionId,
              title: 'Section',
              steps: [],
              sibling_order: '0'
            };
          }
        }
        sourceStep = (sourceSection.steps || []).find(st => st.tree_node_id === sourceNode.stepId || st.step_id_ref === sourceNode.stepId);
        if (!sourceStep) {
          if (!isFromMovePreview) {
            dispatch(showSnackbar({ message: 'Source step not found for child', type: 'error' }));
            dispatch(setLoading(false));
            return;
          } else {
            // Bypass check - create minimal placeholder structure
            console.warn('Source step not found, creating placeholder for MovePreview');
            sourceStep = {
              tree_node_id: sourceNode.stepId,
              step_id_ref: sourceNode.stepId,
              title: 'Step',
              substeps: [],
              children: [],
              sibling_order: '0'
            };
            if (!sourceSection.steps) {
              sourceSection.steps = [];
            }
            sourceSection.steps.push(sourceStep);
          }
        }
        let sourceParent = sourceStep;
        if (sourceNode.substepId) {
          const ss = (sourceStep.substeps || []).find(s => s.tree_node_id === sourceNode.substepId);
          if (ss) {
            sourceParent = ss;
          }
        }
        // Try to find child under the expected parent first
        let srcChildren = sourceParent.children || [];
        // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
        let srcChild = srcChildren.find(c => {
          const nodeId = c.tree_node_id;
          const canvasNodeId = c.detail?.canvas_tree_node_id;
          return nodeId === sourceNode.nodeId || canvasNodeId === sourceNode.nodeId;
        });
        // Fallback: search entire source structure to locate the child and its true parent
        if (!srcChild) {
          outerFindChild: for (const sec of sourceResponse) {
            for (const st of (sec.steps || [])) {
              // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
              const inStep = (st.children || []).find(c => {
                const nodeId = c.tree_node_id;
                const canvasNodeId = c.detail?.canvas_tree_node_id;
                return nodeId === sourceNode.nodeId || canvasNodeId === sourceNode.nodeId;
              });
              if (inStep) { sourceParent = st; srcChildren = st.children || []; srcChild = inStep; break outerFindChild; }
              for (const ss of (st.substeps || [])) {
                // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
                const inSub = (ss.children || []).find(c => {
                  const nodeId = c.tree_node_id;
                  const canvasNodeId = c.detail?.canvas_tree_node_id;
                  return nodeId === sourceNode.nodeId || canvasNodeId === sourceNode.nodeId;
                });
                if (inSub) { sourceParent = ss; srcChildren = ss.children || []; srcChild = inSub; break outerFindChild; }
              }
            }
          }
        }
        if (!srcChild) {
          if (!isFromMovePreview) {
            dispatch(showSnackbar({ message: 'Source child not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          } else {
            // Bypass check - create minimal placeholder structure
            console.warn('Source child not found, creating placeholder for MovePreview');
            srcChild = {
              tree_node_id: sourceNode.nodeId,
              detail: {},
              sibling_order: '0'
            };
            if (!sourceParent.children) {
              sourceParent.children = [];
            }
            sourceParent.children.push(srcChild);
          }
        }
        const clonedChild = JSON.parse(JSON.stringify(srcChild));

        // Insert into target
        if (position === 'into' && targetType === 'step') {
          const tSection = (sortedSections || []).find(sec => (sec.steps || []).some(st => st.tree_node_id === targetId));
          if (!tSection) { dispatch(showSnackbar({ message: 'Target step section not found', type: 'error' })); return; }
          const tStep = (tSection.steps || []).find(st => st.tree_node_id === targetId);
          if (!tStep) { dispatch(showSnackbar({ message: 'Target step not found', type: 'error' })); return; }
          const children = tStep.children || [];
          const sortedChildren = [...children].sort((a,b)=>{
            const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
            const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
            return aO - bO;
          });
          const maxO = sortedChildren.length ? parseInt(sortedChildren[sortedChildren.length-1].sibling_order || sortedChildren[sortedChildren.length-1].procedure_tree?.sibling_order || '0',10) : 0;
          clonedChild.sibling_order = (maxO + 1).toString();
          sortedChildren.push(clonedChild);
          sortedChildren.forEach((c,i)=>{ c.sibling_order = i.toString(); });
          tStep.children = sortedChildren;
          newTargetData = sortedSections;
        } else if (position === 'into' && targetType === 'substep') {
          const tSection = (sortedSections || []).find(sec => (sec.steps || []).some(st => (st.substeps || []).some(ss => ss.tree_node_id === targetId)));
          if (!tSection) { dispatch(showSnackbar({ message: 'Target substep section not found', type: 'error' })); return; }
          const tStep = (tSection.steps || []).find(st => (st.substeps || []).some(ss => ss.tree_node_id === targetId));
          const tSubstep = (tStep?.substeps || []).find(ss => ss.tree_node_id === targetId);
          if (!tStep || !tSubstep) { dispatch(showSnackbar({ message: 'Target substep not found', type: 'error' })); return; }
          const children = tSubstep.children || [];
          const sortedChildren = [...children].sort((a,b)=>{
            const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
            const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
            return aO - bO;
          });
          const maxO = sortedChildren.length ? parseInt(sortedChildren[sortedChildren.length-1].sibling_order || sortedChildren[sortedChildren.length-1].procedure_tree?.sibling_order || '0',10) : 0;
          clonedChild.sibling_order = (maxO + 1).toString();
          sortedChildren.push(clonedChild);
          sortedChildren.forEach((c,i)=>{ c.sibling_order = i.toString(); });
          tSubstep.children = sortedChildren;
          newTargetData = sortedSections;
        } else if ((position === 'above' || position === 'below') && targetType === 'child') {
          // find parent that contains target child
          let parentContainer = null;
          let list = null;
          outer: for (const sec of (sortedSections || [])) {
            for (const st of (sec.steps || [])) {
              const stepChildren = st.children || [];
              // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
              const idxS = stepChildren.findIndex(c => {
                const nodeId = c.tree_node_id;
                const canvasNodeId = c.detail?.canvas_tree_node_id;
                return nodeId === targetId || canvasNodeId === targetId;
              });
              if (idxS !== -1) { parentContainer = st; list = stepChildren; break outer; }
              for (const ss of (st.substeps || [])) {
                const subChildren = ss.children || [];
                // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
                const idxSS = subChildren.findIndex(c => {
                  const nodeId = c.tree_node_id;
                  const canvasNodeId = c.detail?.canvas_tree_node_id;
                  return nodeId === targetId || canvasNodeId === targetId;
                });
                if (idxSS !== -1) { parentContainer = ss; list = subChildren; break outer; }
              }
            }
          }
          if (!parentContainer || !list) { dispatch(showSnackbar({ message: 'Target child not found', type: 'error' })); return; }
          const sortedChildren = [...list].sort((a,b)=>{
            const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
            const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
            return aO - bO;
          });
          // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
          const tIdx = sortedChildren.findIndex(c => {
            const nodeId = c.tree_node_id;
            const canvasNodeId = c.detail?.canvas_tree_node_id;
            return nodeId === targetId || canvasNodeId === targetId;
          });
          if (tIdx === -1) { dispatch(showSnackbar({ message: 'Target child not found in sorted list', type: 'error' })); return; }
          if (position === 'above') sortedChildren.splice(tIdx, 0, clonedChild); else sortedChildren.splice(tIdx + 1, 0, clonedChild);
          sortedChildren.forEach((c,i)=>{ c.sibling_order = i.toString(); });
          parentContainer.children = sortedChildren;
          newTargetData = sortedSections;
        }
      }
      
      // Get a deep clone of the source node (section or step or substep)
      let clonedNode;
      if (sourceNode.nodeType === 'section') {
        clonedNode = JSON.parse(JSON.stringify(sourceSection));
      } else if (sourceNode.nodeType === 'step') {
        clonedNode = JSON.parse(JSON.stringify(sourceStep));
      } else if (sourceNode.nodeType === 'substep') {
        clonedNode = JSON.parse(JSON.stringify(sourceSubstep));
      }
      
      

      // Compute new data based on paste position and node type
      if (sourceNode.nodeType === 'section') {
        // Section copy logic
        const clonedSection = clonedNode;
        if (position === 'into') {
          // Paste into document (add as new section at the end)
          // Update sibling_order to be after all existing sections
          const maxOrder = sortedSections.length > 0 
            ? parseInt(sortedSections[sortedSections.length - 1].sibling_order || sortedSections[sortedSections.length - 1].procedure_tree?.sibling_order || '0', 10)
            : 0;
          clonedSection.sibling_order = (maxOrder + 1).toString();
          sortedSections.push(clonedSection);
          // Reassign sibling orders sequentially
          sortedSections.forEach((section, index) => {
            section.sibling_order = index.toString();
          });
          newTargetData = sortedSections;
        } else if (position === 'above') {
          // Paste above selected section - find target in sorted array
          const targetSectionIndex = sortedSections.findIndex(
            section => section.tree_node_id === targetId
          );
          
          if (targetSectionIndex !== -1) {
            // Insert before target section
            sortedSections.splice(targetSectionIndex, 0, clonedSection);
            
            // Reassign sibling orders sequentially (0, 1, 2, ...)
            sortedSections.forEach((section, index) => {
              section.sibling_order = index.toString();
            });
            newTargetData = sortedSections;
          } else {
            // Target not found, add to end
            const maxOrder = sortedSections.length > 0 
              ? parseInt(sortedSections[sortedSections.length - 1].sibling_order || sortedSections[sortedSections.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;
            clonedSection.sibling_order = (maxOrder + 1).toString();
            sortedSections.push(clonedSection);
            sortedSections.forEach((section, index) => {
              section.sibling_order = index.toString();
            });
            newTargetData = sortedSections;
          }
        } else if (position === 'below') {
          // Paste below selected section - find target in sorted array
          const targetSectionIndex = sortedSections.findIndex(
            section => section.tree_node_id === targetId
          );
          
          if (targetSectionIndex !== -1) {
            // Insert after target section
            sortedSections.splice(targetSectionIndex + 1, 0, clonedSection);
            
            // Reassign sibling orders sequentially (0, 1, 2, ...)
            sortedSections.forEach((section, index) => {
              section.sibling_order = index.toString();
            });
            newTargetData = sortedSections;
          } else {
            // Target not found, add to end
            const maxOrder = sortedSections.length > 0 
              ? parseInt(sortedSections[sortedSections.length - 1].sibling_order || sortedSections[sortedSections.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;
            clonedSection.sibling_order = (maxOrder + 1).toString();
            sortedSections.push(clonedSection);
            sortedSections.forEach((section, index) => {
              section.sibling_order = index.toString();
            });
            newTargetData = sortedSections;
          }
        }
      } else if (sourceNode.nodeType === 'step') {
        // Step copy logic
        const clonedStep = clonedNode;
        
        if (position === 'into') {
          // Paste step into section
          const targetSectionIndex = sortedSections.findIndex(
            section => section.tree_node_id === targetId
          );
          
          if (targetSectionIndex !== -1) {
            const targetSection = sortedSections[targetSectionIndex];
            const targetSteps = targetSection.steps || [];
            
            // Sort steps by sibling_order
            const sortedSteps = [...targetSteps].sort((a, b) => {
              const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
              const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
              return orderA - orderB;
            });
            
            // Get max order and add 1
            const maxOrder = sortedSteps.length > 0
              ? parseInt(sortedSteps[sortedSteps.length - 1].sibling_order || sortedSteps[sortedSteps.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;
            
            clonedStep.sibling_order = (maxOrder + 1).toString();
            sortedSteps.push(clonedStep);
            
            // Reassign sibling orders sequentially
            sortedSteps.forEach((step, index) => {
              step.sibling_order = index.toString();
            });
            
            // Update the target section with new steps
            targetSection.steps = sortedSteps;
            newTargetData = sortedSections;
          } else {
            // Target section not found
            dispatch(showSnackbar({ message: 'Target section not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
        } else if (position === 'above' || position === 'below') {
          // Paste step above or below another step
          // Find the target step's section first
          let targetStepSection = null;
          let targetStep = null;
          let targetStepSectionIndex = -1;
          let targetStepIndex = -1;
          
          for (let i = 0; i < sortedSections.length; i++) {
            const section = sortedSections[i];
            const steps = section.steps || [];
            const stepIndex = steps.findIndex(step => step.tree_node_id === targetId);
            
            if (stepIndex !== -1) {
              targetStepSection = section;
              targetStep = steps[stepIndex];
              targetStepSectionIndex = i;
              targetStepIndex = stepIndex;
              break;
            }
          }
          
          if (!targetStepSection || !targetStep) {
            dispatch(showSnackbar({ message: 'Target step not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
          
          // Get all steps from target section and sort them
          const allSteps = targetStepSection.steps || [];
          const sortedSteps = [...allSteps].sort((a, b) => {
            const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
            const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
            return orderA - orderB;
          });
          
          // Find target step index in sorted array
          const sortedTargetStepIndex = sortedSteps.findIndex(
            step => step.tree_node_id === targetId
          );
          
          if (sortedTargetStepIndex !== -1) {
            if (position === 'above') {
              // Insert before target step
              sortedSteps.splice(sortedTargetStepIndex, 0, clonedStep);
            } else {
              // Insert after target step
              sortedSteps.splice(sortedTargetStepIndex + 1, 0, clonedStep);
            }
            
            // Reassign sibling orders sequentially
            sortedSteps.forEach((step, index) => {
              step.sibling_order = index.toString();
            });
            
            // Update the target section with new steps
            targetStepSection.steps = sortedSteps;
            newTargetData = sortedSections;
          } else {
            dispatch(showSnackbar({ message: 'Target step not found in sorted array', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
        }
      } else if (sourceNode.nodeType === 'substep') {
        // Substep copy logic
        const clonedSubstep = clonedNode;

        if (position === 'into' && targetType === 'step') {
          // Paste substep into a step
          // Find target step
          let targetSection = (sortedSections || []).filter(Boolean).find(section => (section.steps || []).some(st => st.tree_node_id === targetId));
          if (!targetSection) {
            dispatch(showSnackbar({ message: 'Target step section not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
          const targetStep = (targetSection.steps || []).find(st => st.tree_node_id === targetId);
          if (!targetStep) {
            dispatch(showSnackbar({ message: 'Target step not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }

          const substeps = targetStep.substeps || [];
          const sortedSubsteps = [...substeps].sort((a, b) => {
            const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
            const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
            return orderA - orderB;
          });
          const maxOrder = sortedSubsteps.length > 0
            ? parseInt(sortedSubsteps[sortedSubsteps.length - 1].sibling_order || sortedSubsteps[sortedSubsteps.length - 1].procedure_tree?.sibling_order || '0', 10)
            : 0;
          clonedSubstep.sibling_order = (maxOrder + 1).toString();
          sortedSubsteps.push(clonedSubstep);
          sortedSubsteps.forEach((ss, index) => {
            ss.sibling_order = index.toString();
          });
          targetStep.substeps = sortedSubsteps;
          newTargetData = sortedSections;
        } else if ((position === 'above' || position === 'below') && targetType === 'substep') {
          // Paste substep relative to another substep
          // Find the step containing the target substep
          let containingSection = null;
          let containingStep = null;
          for (let i = 0; i < (sortedSections || []).length; i++) {
            const section = sortedSections[i];
            if (!section) continue;
            for (let j = 0; j < ((section.steps || []).length); j++) {
              const step = section.steps[j];
              const idx = (step.substeps || []).findIndex(ss => ss.tree_node_id === targetId);
              if (idx !== -1) {
                containingSection = section;
                containingStep = step;
                break;
              }
            }
            if (containingStep) break;
          }
          if (!containingStep) {
            dispatch(showSnackbar({ message: 'Target substep not found', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }

          const substeps = containingStep.substeps || [];
          const sortedSubsteps = [...substeps].sort((a, b) => {
            const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
            const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
            return orderA - orderB;
          });
          const targetIdx = sortedSubsteps.findIndex(ss => ss.tree_node_id === targetId);
          if (targetIdx === -1) {
            dispatch(showSnackbar({ message: 'Target substep not found in sorted array', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
          if (position === 'above') {
            sortedSubsteps.splice(targetIdx, 0, clonedSubstep);
          } else {
            sortedSubsteps.splice(targetIdx + 1, 0, clonedSubstep);
          }
          sortedSubsteps.forEach((ss, index) => {
            ss.sibling_order = index.toString();
          });
          containingStep.substeps = sortedSubsteps;
          newTargetData = sortedSections;
        }
      }
      
      // Dispatch the computed new data
      dispatch(setNewData(newTargetData));
      
      // Move to preview step
      setStep('preview');
    } catch (error) {
      console.error('Error computing new structure:', error);
      dispatch(showSnackbar({ message: 'Error computing preview: ' + (error?.message || 'Unknown error'), type: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (step === 'preview') {
      if (copyMode === 'node') {
        setStep('pastePosition');
      } else {
        setStep('select');
      }
    } else if (step === 'pastePosition') {
      setStep('select');
    } else if (step === 'confirm') {
      setStep('preview');
    }
  };

  // Handle close
  const handleClose = () => {
    dispatch(closeCopyReplace());
  };

  // Handle save confirmation - Copy & Replace operation
  const handleConfirmSave = async () => {
         if (!comment || !comment.trim()) {
      dispatch(showSnackbar({ message: 'Comment is mandatory.', type: 'error' }));
      return;
    }

    if (!sourceDocument || !targetDocument) {
      dispatch(showSnackbar({ message: 'Source or target document is missing.', type: 'error' }));
      return;
    }

    const childNodeKeys = [ 'attachment','video','rich-text','form','canva','canvas' ];
    dispatch(setLoading(true));
    
    try {
      // For node copy (section, step, substep) — prefer sourceNode presence over copyMode to avoid misrouting
      if (sourceNode) {
        if (!sourceNode) {
          dispatch(showSnackbar({ message: 'Missing source node for copy.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        if (!pastePosition) {
          dispatch(showSnackbar({ message: 'Please select a paste position.', type: 'error' }));
          dispatch(setLoading(false));
          return;
        }
        // Fetch source document data to get source section details
        const sourceAllNodes = await dispatch(getRevAllNodes({ 
          partId: sourceDocument.partId, 
          revisionId: sourceDocument.revisionId 
        })).unwrap();

        // Declare variables used across all branches (including child) BEFORE any assignment
        let toOrder;
        let toProcedureTreeNodeRef;
        let toSectionIdRef;
        let toStepIdRef;
        let toSubstepIdRef;
        let direction;
        let actionType;
        let commentText;
        let fromType;
        let fromTreeId;
        let fromOrder;
        let fromSectionId;
        let fromStepId;
        let fromSubstepId;
        let fromChildNodeRef;
        let toChildType; // Store target child type for paste above/below

        // Get target document data early for use in all branches
        const targetAllNodes = originalData || await dispatch(getRevAllNodes({ 
          partId: targetDocument.partId, 
          revisionId: targetDocument.revisionId 
        })).unwrap();

        // Find source node (section or step or substep)
        let sourceSection = null;
        let sourceStep = null;
        let sourceSubstep = null;
        
        if (sourceNode.nodeType === 'section') {
          // For section copy: find the section directly
          sourceSection = sourceAllNodes.find(
            section => section.tree_node_id === sourceNode.nodeId || section.section_id_ref === sourceNode.nodeId
          );

          if (!sourceSection) {
            if (!isFromMovePreview) {
              dispatch(showSnackbar({ message: 'Source section not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            } else {
              // Bypass check - create minimal placeholder structure
              console.warn('Source section not found, creating placeholder for MovePreview');
              sourceSection = {
                tree_node_id: sourceNode.nodeId,
                section_id_ref: sourceNode.nodeId,
                title: sourceNode.nodeTitle || 'Section',
                steps: [],
                sibling_order: '0'
              };
            }
          }
        } else if (sourceNode.nodeType === 'step') {
          // For step copy: find the section containing the step, then find the step
          sourceSection = sourceAllNodes.find(
            section => section.tree_node_id === sourceNode.sectionId
          );

          if (!sourceSection) {
            if (!isFromMovePreview) {
              dispatch(showSnackbar({ message: 'Source section not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            } else {
              // Bypass check - create minimal placeholder structure
              console.warn('Source section not found, creating placeholder for MovePreview');
              sourceSection = {
                tree_node_id: sourceNode.sectionId,
                section_id_ref: sourceNode.sectionId,
                title: 'Section',
                steps: [],
                sibling_order: '0'
              };
            }
          }
          
          sourceStep = sourceSection.steps?.find(
            step => step.tree_node_id === sourceNode.nodeId || step.step_id_ref === sourceNode.nodeId
          );
          
          if (!sourceStep) {
            if (!isFromMovePreview) {
              dispatch(showSnackbar({ message: 'Source step not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            } else {
              // Bypass check - create minimal placeholder structure
              console.warn('Source step not found, creating placeholder for MovePreview');
              sourceStep = {
                tree_node_id: sourceNode.nodeId,
                step_id_ref: sourceNode.nodeId,
                title: sourceNode.nodeTitle || 'Step',
                substeps: [],
                children: [],
                sibling_order: '0'
              };
              if (!sourceSection.steps) {
                sourceSection.steps = [];
              }
              sourceSection.steps.push(sourceStep);
            }
          }
        } else if (sourceNode.nodeType === 'substep') {
          // For substep copy: find section and step, then substep
          sourceSection = sourceAllNodes.find(
            section => section.tree_node_id === sourceNode.sectionId
          );
          if (!sourceSection) {
            if (!isFromMovePreview) {
              dispatch(showSnackbar({ message: 'Source section not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            } else {
              // Bypass check - create minimal placeholder structure
              console.warn('Source section not found, creating placeholder for MovePreview');
              sourceSection = {
                tree_node_id: sourceNode.sectionId,
                section_id_ref: sourceNode.sectionId,
                title: 'Section',
                steps: [],
                sibling_order: '0'
              };
            }
          }
          sourceStep = sourceSection.steps?.find(
            step => step.tree_node_id === sourceNode.stepId || step.step_id_ref === sourceNode.stepId
          );
          if (!sourceStep) {
            if (!isFromMovePreview) {
              dispatch(showSnackbar({ message: 'Source step not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            } else {
              // Bypass check - create minimal placeholder structure
              console.warn('Source step not found, creating placeholder for MovePreview');
              sourceStep = {
                tree_node_id: sourceNode.stepId,
                step_id_ref: sourceNode.stepId,
                title: 'Step',
                substeps: [],
                children: [],
                sibling_order: '0'
              };
              if (!sourceSection.steps) {
                sourceSection.steps = [];
              }
              sourceSection.steps.push(sourceStep);
            }
          }
          sourceSubstep = sourceStep.substeps?.find(
            ss => ss.tree_node_id === sourceNode.nodeId || ss.substep_id_ref === sourceNode.nodeId
          );
          if (!sourceSubstep) {
            if (!isFromMovePreview) {
              dispatch(showSnackbar({ message: 'Source substep not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            } else {
              // Bypass check - create minimal placeholder structure
              console.warn('Source substep not found, creating placeholder for MovePreview');
              sourceSubstep = {
                tree_node_id: sourceNode.nodeId,
                substep_id_ref: sourceNode.nodeId,
                title: sourceNode.nodeTitle || 'Substep',
                children: [],
                sibling_order: '0'
              };
              if (!sourceStep.substeps) {
                sourceStep.substeps = [];
              }
              sourceStep.substeps.push(sourceSubstep);
            }
          }
        } else if (childNodeKeys.includes(sourceNode.nodeType)) {
          // Child node copy payload
          const childType = sourceNode.nodeType === 'canvas' ? 'canva' : sourceNode.nodeType;
          // Resolve source context
          sourceSection = sourceAllNodes.find(sec => sec.tree_node_id === sourceNode.sectionId || sec.section_id_ref === sourceNode.sectionId);
          if (!sourceSection) { 
            if (!isFromMovePreview) {
              dispatch(showSnackbar({ message: 'Source section not found.', type: 'error' }));
              dispatch(setLoading(false)); 
              return;
            } else {
              // Bypass check - create minimal placeholder structure
              console.warn('Source section not found, creating placeholder for MovePreview');
              sourceSection = {
                tree_node_id: sourceNode.sectionId,
                section_id_ref: sourceNode.sectionId,
                title: 'Section',
                steps: [],
                sibling_order: '0'
              };
            }
          }
          sourceStep = (sourceSection.steps || []).find(st => st.tree_node_id === sourceNode.stepId || st.step_id_ref === sourceNode.stepId);
          if (!sourceStep) { 
            if (!isFromMovePreview) {
              dispatch(showSnackbar({ message: 'Source step not found.', type: 'error' })); 
              dispatch(setLoading(false)); 
              return;
            } else {
              // Bypass check - create minimal placeholder structure
              console.warn('Source step not found, creating placeholder for MovePreview');
              sourceStep = {
                tree_node_id: sourceNode.stepId,
                step_id_ref: sourceNode.stepId,
                title: 'Step',
                substeps: [],
                children: [],
                sibling_order: '0'
              };
              if (!sourceSection.steps) {
                sourceSection.steps = [];
              }
              sourceSection.steps.push(sourceStep);
            }
          }
          let sourceParent = sourceStep;
          if (sourceNode.substepId) {
            const ss = (sourceStep.substeps || []).find(s => s.tree_node_id === sourceNode.substepId);
            if (ss) {
              sourceParent = ss;
            }
          }
          // Try expected parent first
          // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
          let srcChild = (sourceParent.children || []).find(c => {
            const nodeId = c.tree_node_id;
            const canvasNodeId = c.detail?.canvas_tree_node_id;
            return nodeId === sourceNode.nodeId || canvasNodeId === sourceNode.nodeId;
          });
          // Fallback: search whole tree for the child and recover its parent
          if (!srcChild) {
            outerFindChild2: for (const sec of sourceAllNodes) {
              for (const st of (sec.steps || [])) {
                // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
                const inStep = (st.children || []).find(c => {
                  const nodeId = c.tree_node_id;
                  const canvasNodeId = c.detail?.canvas_tree_node_id;
                  return nodeId === sourceNode.nodeId || canvasNodeId === sourceNode.nodeId;
                });
                if (inStep) { sourceParent = st; srcChild = inStep; break outerFindChild2; }
                for (const ss of (st.substeps || [])) {
                  // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
                  const inSub = (ss.children || []).find(c => {
                    const nodeId = c.tree_node_id;
                    const canvasNodeId = c.detail?.canvas_tree_node_id;
                    return nodeId === sourceNode.nodeId || canvasNodeId === sourceNode.nodeId;
                  });
                  if (inSub) { sourceParent = ss; srcChild = inSub; break outerFindChild2; }
                }
              }
            }
          }
          if (!srcChild) { 
            if (!isFromMovePreview) {
              dispatch(showSnackbar({ message: 'Source child not found.', type: 'error' })); 
              dispatch(setLoading(false)); 
              return;
            } else {
              // Bypass check - create minimal placeholder structure
              console.warn('Source child not found, creating placeholder for MovePreview');
              srcChild = {
                tree_node_id: sourceNode.nodeId,
                detail: {},
                sibling_order: '0'
              };
              if (!sourceParent.children) {
                sourceParent.children = [];
              }
              sourceParent.children.push(srcChild);
            }
          }

          fromType = childType;
          fromTreeId = srcChild.tree_node_id;
          fromOrder = parseInt(srcChild.sibling_order || srcChild.procedure_tree?.sibling_order || '0', 10);
          fromSectionId = sourceSection.section_id_ref;
          fromStepId = sourceStep.step_id_ref || sourceStep.tree_node_id;
          fromSubstepId = srcChild.substep_id_ref || '';
          fromChildNodeRef = srcChild.type === "attachment" ? 
            srcChild?.detail?.attachment_tree_node_id : (srcChild.type === "canvas" || srcChild.type === "canva") ? 
            srcChild?.detail?.canvas_tree_node_id : srcChild.type === "rich-text" ? 
            srcChild?.detail?.rich_text_node_id : srcChild.type === "video" ? srcChild?.detail?.video_tree_node_id : ''; 


          if (pastePosition.position === 'into' && pastePosition.targetType === 'step') {
            // Paste child into a step (append)
            const targetSection = originalData.find(sec => (sec.steps || []).some(st => st.tree_node_id === pastePosition.targetId));
            if (!targetSection) { dispatch(showSnackbar({ message: 'Target section not found.', type: 'error' })); dispatch(setLoading(false)); return; }
            const targetStep = (targetSection.steps || []).find(st => st.tree_node_id === pastePosition.targetId);
            if (!targetStep) { dispatch(showSnackbar({ message: 'Target step not found.', type: 'error' })); dispatch(setLoading(false)); return; }
            const children = targetStep.children || [];
            const sortedChildren = [...children].sort((a,b)=>{
              const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
              const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
              return aO - bO;
            });
            // Case 1: to_order = total children + 1
            toOrder = sortedChildren.length + 1;
            toProcedureTreeNodeRef = targetSection.tree_node_id;
            toSectionIdRef = targetSection.section_id_ref;
            toStepIdRef = targetStep.step_id_ref || targetStep.tree_node_id;
            toSubstepIdRef = '';
            direction = 'paste_into';
            actionType = 'paste_into';
            commentText = comment;
          } else if (pastePosition.position === 'into' && pastePosition.targetType === 'substep') {
            // Paste child into a substep (append)
            let targetSection = null; let targetStep = null; let targetSubstep = null;
            for (const sec of originalData) {
              for (const st of (sec.steps || [])) {
                const match = (st.substeps || []).find(ss => ss.tree_node_id === pastePosition.targetId);
                if (match) { targetSection = sec; targetStep = st; targetSubstep = match; break; }
              }
              if (targetSubstep) break;
            }
            if (!targetSection || !targetStep || !targetSubstep) { dispatch(showSnackbar({ message: 'Target substep not found.', type: 'error' })); dispatch(setLoading(false)); return; }
            const children = targetSubstep.children || [];
            const sortedChildren = [...children].sort((a,b)=>{
              const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
              const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
              return aO - bO;
            });
            // Case 1: to_order = total children + 1
            toOrder = sortedChildren.length + 1;
            toProcedureTreeNodeRef = targetSection.tree_node_id;
            toSectionIdRef = targetSection.section_id_ref;
            toStepIdRef = targetStep.step_id_ref || targetStep.tree_node_id;
            toSubstepIdRef = targetSubstep.substep_id_ref || targetSubstep.tree_node_id;
            direction = 'paste_into';
            actionType = 'paste_into';
            commentText = comment;
          } else if ((pastePosition.position === 'above' || pastePosition.position === 'below') && pastePosition.targetType === 'child') {
            // Paste child relative to another child
            let parent = null; let list = null; let targetChild = null;
            outer: for (const sec of originalData) {
              for (const st of (sec.steps || [])) {
                // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
                const inStep = (st.children || []).find(c => {
                  const nodeId = c.tree_node_id;
                  const canvasNodeId = c.detail?.canvas_tree_node_id;
                  return nodeId === pastePosition.targetId || canvasNodeId === pastePosition.targetId;
                });
                if (inStep) { parent = { type:'step', section: sec, step: st }; list = st.children || []; targetChild = inStep; break outer; }
                for (const ss of (st.substeps || [])) {
                  // For canvas nodes, check both tree_node_id and detail.canvas_tree_node_id
                  const inSub = (ss.children || []).find(c => {
                    const nodeId = c.tree_node_id;
                    const canvasNodeId = c.detail?.canvas_tree_node_id;
                    return nodeId === pastePosition.targetId || canvasNodeId === pastePosition.targetId;
                  });
                  if (inSub) { parent = { type:'substep', section: sec, step: st, substep: ss }; list = ss.children || []; targetChild = inSub; break outer; }
                }
              }
            }
            if (!parent || !list || !targetChild) { dispatch(showSnackbar({ message: 'Target child not found.', type: 'error' })); dispatch(setLoading(false)); return; }
            const sortedChildren = [...list].sort((a,b)=>{
              const aO = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0',10);
              const bO = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0',10);
              return aO - bO;
            });
            const targetOrder = parseInt(targetChild.sibling_order || targetChild.procedure_tree?.sibling_order || '0', 10);
            // Case 2: Relative positioning
            if (pastePosition.position === 'below') {
              // Below any child: to_order = target child's sibling_order + 1
              toOrder = parseInt(targetOrder, 10) + 1;
            } else {
              // Above (paste_up)
              const parsedOrder = parseInt(targetOrder, 10);
              // If target child's sibling_order is 1, to_order = 1
              // Otherwise, to_order = sibling_order - 1
              if (parsedOrder === 1) {
                toOrder = 1;
              } else {
                toOrder = parsedOrder;
              }
            }
            toSectionIdRef = parent.section.section_id_ref;
            toStepIdRef = parent.step.step_id_ref || parent.step.tree_node_id;
            toSubstepIdRef = parent.type === 'substep' ? (parent.substep.substep_id_ref || parent.substep.tree_node_id) : '';
            toProcedureTreeNodeRef = parent.section.tree_node_id;
            // Store target child type for to_type determination
            toChildType = targetChild.type === 'canvas' ? 'canva' : targetChild.type;
            direction = pastePosition.position === 'above' ? 'paste_up' : 'paste_down';
            actionType = pastePosition.position === 'above' ? 'paste_up' : 'paste_down';
            commentText = comment;
          } else {
            dispatch(showSnackbar({ message: 'Invalid paste position for child copy.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
        }

        // Calculate to_order and other target values based on node type and paste position

        if (sourceNode.nodeType === 'section') {
          // Section copy logic
          fromType = "section";
          fromTreeId = sourceSection.tree_node_id;
          fromOrder = parseInt(sourceSection.sibling_order || sourceSection.procedure_tree?.sibling_order || '0', 10);
          fromSectionId = sourceSection.section_id_ref;
          fromStepId = "";
          fromSubstepId = "";

          if (pastePosition.position === 'into') {
            // Paste section into document (at the end)
            const sortedSections = [...originalData].sort((a, b) => {
              const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
              const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
              return orderA - orderB;
            });
            
            const highestOrder = sortedSections.length > 0
              ? parseInt(sortedSections[sortedSections.length - 1].sibling_order || sortedSections[sortedSections.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;
            
            toOrder = parseInt(highestOrder, 10) + 1;
            toProcedureTreeNodeRef = targetDocument.revisionId; // Use revisionId for document root
            toSectionIdRef = null;
            toStepIdRef = "";
            toSubstepIdRef = "";
            direction = 'paste_down';
            actionType = 'paste_down';
            commentText = `Copied ${sourceNode.nodeTitle} into ${targetDocument.type} ${targetDocument.partNumber} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
          } else {
            // Paste section above or below a section
            const targetSection = originalData.find(
              section => section.tree_node_id === pastePosition.targetId
            );

            if (!targetSection) {
              dispatch(showSnackbar({ message: 'Target section not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }

            const targetOrder = parseInt(targetSection.sibling_order || targetSection.procedure_tree?.sibling_order || '0', 10);
            
            if (pastePosition.position === 'above') {
              toOrder = parseInt(targetOrder, 10);
              direction = 'paste_up';
              actionType = 'paste_up';
              commentText = `Copied ${sourceNode.nodeTitle} above Section ${pastePosition.targetIndex + 1} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
            } else {
              // below
              toOrder = parseInt(targetOrder, 10) + 1;
              direction = 'paste_down';
              actionType = 'paste_down';
              commentText = `Copied ${sourceNode.nodeTitle} below Section ${pastePosition.targetIndex + 1} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
            }

            toProcedureTreeNodeRef = targetSection.tree_node_id;
            toSectionIdRef = targetSection.section_id_ref;
            toStepIdRef = "";
            toSubstepIdRef = "";
          }
        } else if (sourceNode.nodeType === 'step') {
          // Step copy logic
          fromType = "step";
          fromTreeId = sourceStep.tree_node_id;
          fromOrder = parseInt(sourceStep.sibling_order || sourceStep.procedure_tree?.sibling_order || '0', 10);
          fromSectionId = sourceSection.section_id_ref;
          fromStepId = sourceStep.step_id_ref || sourceStep.tree_node_id;
          fromSubstepId = "";

          if (pastePosition.position === 'into' && pastePosition.targetType === 'section') {
            // Paste step into section
            const targetSection = originalData.find(
              section => section.tree_node_id === pastePosition.targetId
            );

            if (!targetSection) {
              dispatch(showSnackbar({ message: 'Target section not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }

            // Get max step order in target section
            const targetSteps = targetSection.steps || [];
            const sortedSteps = [...targetSteps].sort((a, b) => {
              const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
              const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
              return orderA - orderB;
            });

            const maxOrder = sortedSteps.length > 0
              ? parseInt(sortedSteps[sortedSteps.length - 1].sibling_order || sortedSteps[sortedSteps.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;

            toOrder = parseInt(maxOrder, 10) + 1;
            toProcedureTreeNodeRef = targetSection.tree_node_id;
            toSectionIdRef = targetSection.section_id_ref;
            toStepIdRef = ""; // Empty when pasting into section
            toSubstepIdRef = "";
            direction = 'paste_down';
            actionType = 'paste_down';
            commentText = `Copied ${sourceNode.nodeTitle} into Section ${pastePosition.targetIndex + 1} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
          } else if ((pastePosition.position === 'above' || pastePosition.position === 'below') && pastePosition.targetType === 'step') {
            // Paste step above or below another step
            // Find the target step's section
            let targetStepSection = null;
            let targetStep = null;

            for (const section of originalData) {
              const steps = section.steps || [];
              const step = steps.find(s => s.tree_node_id === pastePosition.targetId);
              
              if (step) {
                targetStepSection = section;
                targetStep = step;
                break;
              }
            }

            if (!targetStepSection || !targetStep) {
              dispatch(showSnackbar({ message: 'Target step not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }

            const targetStepOrder = parseInt(targetStep.sibling_order || targetStep.procedure_tree?.sibling_order || '0', 10);

            if (pastePosition.position === 'above') {
              toOrder = parseInt(targetStepOrder, 10);
              direction = 'paste_up';
              actionType = 'paste_up';
              commentText = `Copied ${sourceNode.nodeTitle} above Step ${pastePosition.globalStepNumber} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
            } else {
              // below
              toOrder = parseInt(targetStepOrder, 10) + 1;
              direction = 'paste_down';
              actionType = 'paste_down';
              commentText = `Copied ${sourceNode.nodeTitle} below Step ${pastePosition.globalStepNumber} in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
            }

            toProcedureTreeNodeRef = targetStepSection.tree_node_id;
            toSectionIdRef = targetStepSection.section_id_ref;
            toStepIdRef = targetStep.step_id_ref || targetStep.tree_node_id;
            toSubstepIdRef = "";
          } else {
            dispatch(showSnackbar({ message: 'Invalid paste position for step copy.', type: 'error' }));
            dispatch(setLoading(false));
            return;
          }
        } else if (sourceNode.nodeType === 'substep') {
          // Substep copy logic
          fromType = "substep";
          const sourceAllNodesStep = sourceStep;
          const sourceAllNodesSubstep = sourceSubstep;
          if (!sourceAllNodesStep || !sourceAllNodesSubstep) {
            if (!isFromMovePreview) {
              dispatch(showSnackbar({ message: 'Source step or substep not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            } else {
              // Bypass check - continue anyway
              console.warn('Source step or substep not found, continuing from MovePreview');
            }
          }
          fromTreeId = sourceAllNodesSubstep.tree_node_id;
          fromOrder = parseInt(sourceAllNodesSubstep.sibling_order || sourceAllNodesSubstep.procedure_tree?.sibling_order || '0', 10);
          fromSectionId = sourceSection.section_id_ref;
          fromStepId = sourceAllNodesStep.step_id_ref || sourceAllNodesStep.tree_node_id;
          fromSubstepId = sourceAllNodesSubstep.substep_id_ref || sourceAllNodesSubstep.tree_node_id;

          if (pastePosition.position === 'into' && pastePosition.targetType === 'step') {
            // Paste into a step (append at end)
            const targetSection = originalData.find(sec => sec.steps?.some(st => st.tree_node_id === pastePosition.targetId));
            if (!targetSection) {
              dispatch(showSnackbar({ message: 'Target section not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }
            const targetStep = (targetSection.steps || []).find(st => st.tree_node_id === pastePosition.targetId);
            if (!targetStep) {
              dispatch(showSnackbar({ message: 'Target step not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }
            const substeps = targetStep.substeps || [];
            const sortedSubsteps = [...substeps].sort((a, b) => {
              const orderA = parseInt(a.sibling_order || a.procedure_tree?.sibling_order || '0', 10);
              const orderB = parseInt(b.sibling_order || b.procedure_tree?.sibling_order || '0', 10);
              return orderA - orderB;
            });
            const highestOrder = sortedSubsteps.length > 0
              ? parseInt(sortedSubsteps[sortedSubsteps.length - 1].sibling_order || sortedSubsteps[sortedSubsteps.length - 1].procedure_tree?.sibling_order || '0', 10)
              : 0;
            toOrder = parseInt(highestOrder, 10) + 1;
            toSectionIdRef = targetSection.section_id_ref || targetSection.tree_node_id;
            toStepIdRef = targetStep.step_id_ref || targetStep.tree_node_id;
            toSubstepIdRef = "";
            toProcedureTreeNodeRef = targetStep.tree_node_id;
            direction = "down";
            actionType = "REVISION_NODE_COPIED";
            commentText = comment;
          } else if ((pastePosition.position === 'above' || pastePosition.position === 'below') && pastePosition.targetType === 'substep') {
            // Paste relative to a substep
            let targetSection = null;
            let targetStep = null;
            let targetSubstep = null;
            for (let i = 0; i < originalData.length; i++) {
              const section = originalData[i];
              for (let j = 0; j < (section.steps || []).length; j++) {
                const step = section.steps[j];
                const match = (step.substeps || []).find(ss => ss.tree_node_id === pastePosition.targetId);
                if (match) {
                  targetSection = section;
                  targetStep = step;
                  targetSubstep = match;
                  break;
                }
              }
              if (targetSubstep) break;
            }
            if (!targetSection || !targetStep || !targetSubstep) {
              dispatch(showSnackbar({ message: 'Target substep not found.', type: 'error' }));
              dispatch(setLoading(false));
              return;
            }
            // Determine order based on target substep order and direction
            const targetOrder = parseInt(targetSubstep.sibling_order || targetSubstep.procedure_tree?.sibling_order || '0', 10);
            toOrder = pastePosition.position === 'above' ? parseInt(targetOrder, 10) : parseInt(targetOrder, 10) + 1;
            toSectionIdRef = targetSection.section_id_ref || targetSection.tree_node_id;
            toStepIdRef = targetStep.step_id_ref || targetStep.tree_node_id;
            toSubstepIdRef = targetSubstep.substep_id_ref || targetSubstep.tree_node_id;
            toProcedureTreeNodeRef = targetSubstep.tree_node_id;
            direction = pastePosition.position === 'above' ? 'up' : 'down';
            actionType = "REVISION_NODE_COPIED";
            commentText = comment;
          }
        }


        // Generate node_indexing for copy operations and prepare new content
        let nodeIndexing = null;
        let newContentForVersion = null;
        let actionToShow = '';
        
        if (sourceNode.nodeType === 'section') {
          // Prepare new content after copy
          const newContent = prepareCopyNewContent({
            targetAllNodes: targetAllNodes,
            sourceSection: sourceSection,
            targetDoc: targetDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
            toSectionIdRef: toSectionIdRef,
          });

          // Store newContent for version_content
          newContentForVersion = newContent;

          // Generate node_indexing
          nodeIndexing = buildNodeIndexingForSectionCopy({
            sourceSection,
            sourceAllNodes,
            newContent,
            targetAllNodes,
            targetDoc: targetDocument,
            sourceDoc: sourceDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
          });

          // Build action_to_show for section copy
          const sourceIndexMap = generateNodeIndexing(sourceAllNodes);
          const targetIndexMap = generateNodeIndexing(targetAllNodes);
          
          const sourceSectionName = sourceIndexMap[sourceSection.tree_node_id] || 
            sourceIndexMap[sourceSection.section_id_ref] || 
            `Section ${(sourceAllNodes.findIndex(s => s.tree_node_id === sourceSection.tree_node_id) + 1)}`;
          
          const sourceDocInfo = `from ${sourceDocument.partNumber}, Rev ${sourceDocument.revision}`;
          const targetDocInfo = `into ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
          
          if (pastePosition.position === 'into') {
            actionToShow = `User Copied ${sourceSectionName} ${sourceDocInfo} ${targetDocInfo}`;
          } else {
            // For above/below, find target section name
            // Use pastePosition.targetIndex if available (the index selected by user)
            // Otherwise fall back to finding it from the structure
            let targetSectionName = 'the target section';
            if (pastePosition.targetIndex !== null && pastePosition.targetIndex !== undefined) {
              // Use the index that was selected by the user (0-based, so add 1)
              targetSectionName = `Section ${pastePosition.targetIndex + 1}`;
            } else {
              // Fallback: find target section from structure
              const targetSection = targetAllNodes.find(
                section => section.tree_node_id === pastePosition.targetId
              );
              if (targetSection) {
                targetSectionName = targetIndexMap[targetSection.tree_node_id] || 
                  targetIndexMap[targetSection.section_id_ref] || 
                  `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === targetSection.tree_node_id) + 1)}`;
              }
            }
            
            const positionWord = pastePosition.position === 'above' ? 'above' : 'below';
            actionToShow = `User Copied ${sourceSectionName} ${sourceDocInfo} ${positionWord} ${targetSectionName} ${targetDocInfo}`;
          }
        } else if (sourceNode.nodeType === 'step') {
          // Prepare new content after step copy
          const newContent = prepareStepMoveNewContent({
            targetAllNodes: targetAllNodes,
            sourceStep: sourceStep,
            moveDocument: sourceDocument,
            targetDoc: targetDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
            toSectionIdRef: toSectionIdRef,
            moveCreateCopy: true, // This is a copy operation
          });

          // Store newContent for version_content
          newContentForVersion = newContent;

          // Generate node_indexing for step copy
          nodeIndexing = buildNodeIndexingForStepCopy({
            sourceStep,
            sourceAllNodes,
            newContent,
            targetAllNodes,
            targetDoc: targetDocument,
            sourceDoc: sourceDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
          });

       
          actionToShow = Object.values(nodeIndexing)[0]?.title?.split("System Performs Operation")[0].trim();
        } else if (sourceNode.nodeType === 'substep') {
          // Prepare new content after substep copy
          const newContent = prepareSubstepMoveNewContent({
            targetAllNodes: targetAllNodes,
            sourceSubstep: sourceSubstep,
            moveDocument: sourceDocument,
            targetDoc: targetDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
            toStepIdRef: toStepIdRef,
            moveCreateCopy: true, // This is a copy operation
          });

          // Store newContent for version_content
          newContentForVersion = newContent;

          // Generate node_indexing for substep copy
          nodeIndexing = buildNodeIndexingForSubstepCopy({
            sourceSubstep,
            sourceAllNodes,
            newContent,
            targetDoc: targetDocument,
            sourceDoc: sourceDocument,
            position: pastePosition.position,
            movePosition: pastePosition,
            targetAllNodesOriginal: targetAllNodes,
          });

          // Build action_to_show for substep copy
          const sourceIndexMap = generateNodeIndexing(sourceAllNodes);
          const targetIndexMap = generateNodeIndexing(targetAllNodes);
          
          // Find source section and source step that contains the source substep
          let sourceSection = null;
          let sourceStep = null;
          for (const section of sourceAllNodes) {
            for (const step of section.steps || []) {
              const substep = (step.substeps || []).find(
                s => s.tree_node_id === sourceSubstep.tree_node_id || s.substep_id_ref === sourceSubstep.substep_id_ref
              );
              if (substep) {
                sourceSection = section;
                sourceStep = step;
                break;
              }
            }
            if (sourceStep) break;
          }
          
          const sourceSectionName = sourceSection 
            ? (sourceIndexMap[sourceSection.tree_node_id] || 
               sourceIndexMap[sourceSection.section_id_ref] || 
               `Section ${(sourceAllNodes.findIndex(s => s.tree_node_id === sourceSection.tree_node_id) + 1)}`)
            : 'the source section';
          
          const sourceStepName = sourceStep 
            ? (sourceIndexMap[sourceStep.tree_node_id] || 
               sourceIndexMap[sourceStep.step_id_ref] || 
               `Step ${(sourceAllNodes.reduce((acc, section) => {
                 const stepIndex = (section.steps || []).findIndex(s => s.tree_node_id === sourceStep.tree_node_id || s.step_id_ref === sourceStep.step_id_ref);
                 if (stepIndex !== -1) {
                   const stepsBefore = sourceAllNodes.slice(0, sourceAllNodes.indexOf(section))
                     .reduce((sum, s) => sum + (s.steps || []).length, 0);
                   return stepsBefore + stepIndex + 1;
                 }
                 return acc;
               }, 0))}`)
            : 'the source step';
          
          const sourceSubstepName = sourceIndexMap[sourceSubstep.tree_node_id] || 
            sourceIndexMap[sourceSubstep.substep_id_ref] || 
            `Substep in ${sourceStepName}`;
          
          const sourceDocInfo = `in ${sourceDocument.partNumber}, Rev ${sourceDocument.revision}`;
          const targetDocInfo = `${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
          
          if (pastePosition.position === 'into') {
            // Find target step name
            let targetStep = null;
            let targetSection = null;
            for (const section of targetAllNodes) {
              targetStep = (section.steps || []).find(
                step => step.tree_node_id === pastePosition.targetId || step.step_id_ref === pastePosition.targetId
              );
              if (targetStep) {
                targetSection = section;
                break;
              }
            }
            const targetStepName = targetStep 
              ? (targetIndexMap[targetStep.tree_node_id] || 
                 targetIndexMap[targetStep.step_id_ref] || 
                 `Step ${pastePosition.globalStepNumber || ''}`)
              : 'the target step';
            
            const targetSectionName = targetSection 
              ? (targetIndexMap[targetSection.tree_node_id] || 
                 targetIndexMap[targetSection.section_id_ref] || 
                 `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === targetSection.tree_node_id) + 1)}`)
              : 'the target section';
            
            actionToShow = `User Copied ${sourceSubstepName} from ${sourceStepName} of ${sourceSectionName} ${sourceDocInfo}, into ${targetStepName} of ${targetSectionName} in ${targetDocInfo}`;
          } else {
            // For above/below, find target substep name
            let targetSubstep = null;
            let targetStep = null;
            let targetSection = null;
            for (const section of targetAllNodes) {
              for (const step of section.steps || []) {
                targetSubstep = (step.substeps || []).find(
                  substep => substep.tree_node_id === pastePosition.targetId || substep.substep_id_ref === pastePosition.targetId
                );
                if (targetSubstep) {
                  targetStep = step;
                  targetSection = section;
                  break;
                }
              }
              if (targetSubstep) break;
            }
            
            const targetSubstepName = targetSubstep 
              ? (targetIndexMap[targetSubstep.tree_node_id] || 
                 targetIndexMap[targetSubstep.substep_id_ref] || 
                 `Substep in ${targetIndexMap[targetStep?.tree_node_id] || targetIndexMap[targetStep?.step_id_ref] || 'Step'}`)
              : 'the target substep';
            
            const targetStepName = targetStep 
              ? (targetIndexMap[targetStep.tree_node_id] || 
                 targetIndexMap[targetStep.step_id_ref] || 
                 `Step ${pastePosition.globalStepNumber || ''}`)
              : 'the target step';
            
            const targetSectionName = targetSection 
              ? (targetIndexMap[targetSection.tree_node_id] || 
                 targetIndexMap[targetSection.section_id_ref] || 
                 `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === targetSection.tree_node_id) + 1)}`)
              : 'the target section';
            
            const positionWord = pastePosition.position === 'above' ? 'above' : 'below';
            actionToShow = `User Copied ${sourceSubstepName} from ${sourceStepName} of ${sourceSectionName} ${sourceDocInfo}, ${positionWord} ${targetSubstepName} in ${targetStepName} of ${targetSectionName} in ${targetDocInfo}`;
          }
        } else if ([ 'attachment','video','rich-text','form','canva','canvas' ].includes(sourceNode.nodeType)) {
          // For child nodes, build proper action_to_show
          const childType = sourceNode.nodeType === 'canvas' ? 'canva' : sourceNode.nodeType;
          const childTypeLabel = formatChildTypeLabel(childType);
          
          // Re-find source child node and parent (they were defined in the earlier block)
          let srcChild = null;
          let sourceParent = null;
          let sourceSectionForChild = sourceAllNodes.find(sec => sec.tree_node_id === sourceNode.sectionId);
          let sourceStepForChild = sourceSectionForChild ? (sourceSectionForChild.steps || []).find(st => st.tree_node_id === sourceNode.stepId) : null;
          
          if (sourceStepForChild) {
            sourceParent = sourceStepForChild;
            if (sourceNode.substepId) {
              const ss = (sourceStepForChild.substeps || []).find(s => s.tree_node_id === sourceNode.substepId);
              if (ss) {
                sourceParent = ss;
              }
            }
            
            // Find the child node
            srcChild = (sourceParent.children || []).find(c => {
              const nodeId = c.tree_node_id;
              const canvasNodeId = c.detail?.canvas_tree_node_id;
              return nodeId === sourceNode.nodeId || canvasNodeId === sourceNode.nodeId;
            });
            
            // Fallback: search whole tree for the child
            if (!srcChild) {
              outerFindChild3: for (const sec of sourceAllNodes) {
                for (const st of (sec.steps || [])) {
                  const inStep = (st.children || []).find(c => {
                    const nodeId = c.tree_node_id;
                    const canvasNodeId = c.detail?.canvas_tree_node_id;
                    return nodeId === sourceNode.nodeId || canvasNodeId === sourceNode.nodeId;
                  });
                  if (inStep) {
                    sourceParent = st;
                    sourceStepForChild = st;
                    sourceSectionForChild = sec;
                    srcChild = inStep;
                    break outerFindChild3;
                  }
                  for (const ss of (st.substeps || [])) {
                    const inSub = (ss.children || []).find(c => {
                      const nodeId = c.tree_node_id;
                      const canvasNodeId = c.detail?.canvas_tree_node_id;
                      return nodeId === sourceNode.nodeId || canvasNodeId === sourceNode.nodeId;
                    });
                    if (inSub) {
                      sourceParent = ss;
                      sourceStepForChild = st;
                      sourceSectionForChild = sec;
                      srcChild = inSub;
                      break outerFindChild3;
                    }
                  }
                }
              }
            }
          }
          
          if (!srcChild) {
            // Fallback action_to_show if child not found
            actionToShow = `User Copied ${childTypeLabel} from ${sourceDocument.partNumber}, Rev ${sourceDocument.revision} into ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
            newContentForVersion = targetAllNodes;
          } else {
            // Get source child node display number
            const sourceChildNumber = getChildNodeDisplayNumber(srcChild, childType, sourceAllNodes);
            
            // Get source section and step names
            const sourceIndexMap = generateNodeIndexing(sourceAllNodes);
            const sourceSectionName = sourceSectionForChild 
              ? (sourceIndexMap[sourceSectionForChild.tree_node_id] || 
                 sourceIndexMap[sourceSectionForChild.section_id_ref] || 
                 `Section ${(sourceAllNodes.findIndex(s => s.tree_node_id === sourceSectionForChild.tree_node_id) + 1)}`)
              : 'the source section';
            
            const sourceStepName = sourceStepForChild 
              ? (sourceIndexMap[sourceStepForChild.tree_node_id] || 
                 sourceIndexMap[sourceStepForChild.step_id_ref] || 
                 `Step ${(sourceAllNodes.reduce((acc, section) => {
                   const stepIndex = (section.steps || []).findIndex(s => s.tree_node_id === sourceStepForChild.tree_node_id || s.step_id_ref === sourceStepForChild.step_id_ref);
                   if (stepIndex !== -1) {
                     const stepsBefore = sourceAllNodes.slice(0, sourceAllNodes.indexOf(section))
                       .reduce((sum, s) => sum + (s.steps || []).length, 0);
                     return stepsBefore + stepIndex + 1;
                   }
                   return acc;
                 }, 0))}`)
              : 'the source step';
            
            // Check if source child is in a substep
            let sourceSubstepName = '';
            if (sourceNode.substepId && sourceParent !== sourceStepForChild && sourceParent.substep_id_ref) {
              const sourceSubstep = sourceParent;
              sourceSubstepName = sourceIndexMap[sourceSubstep.tree_node_id] || 
                sourceIndexMap[sourceSubstep.substep_id_ref] || 
                `Substep in ${sourceStepName}`;
            }
          
          const sourceDocInfo = `from ${sourceDocument.partNumber}, Rev ${sourceDocument.revision}`;
          const targetDocInfo = `in ${targetDocument.partNumber}, Rev ${targetDocument.revision}`;
          
          // Get target information
          const targetIndexMap = generateNodeIndexing(targetAllNodes);
          
          if (pastePosition.position === 'into' && pastePosition.targetType === 'step') {
            // Copy into a step
            const targetSection = targetAllNodes.find(sec => (sec.steps || []).some(st => st.tree_node_id === pastePosition.targetId));
            const targetStep = targetSection ? (targetSection.steps || []).find(st => st.tree_node_id === pastePosition.targetId) : null;
            
            const targetSectionName = targetSection 
              ? (targetIndexMap[targetSection.tree_node_id] || 
                 targetIndexMap[targetSection.section_id_ref] || 
                 `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === targetSection.tree_node_id) + 1)}`)
              : 'the target section';
            
            const targetStepName = targetStep 
              ? (targetIndexMap[targetStep.tree_node_id] || 
                 targetIndexMap[targetStep.step_id_ref] || 
                 `Step ${pastePosition.globalStepNumber || ''}`)
              : 'the target step';
            
            const sourceContext = sourceSubstepName 
              ? `from ${sourceSubstepName} in ${sourceStepName} in ${sourceSectionName}`
              : `from ${sourceStepName} in ${sourceSectionName}`;
            
            actionToShow = `User Copied ${childTypeLabel} ${sourceChildNumber} ${sourceContext} ${sourceDocInfo} into ${targetStepName} in ${targetSectionName} ${targetDocInfo}`;
          } else if (pastePosition.position === 'into' && pastePosition.targetType === 'substep') {
            // Copy into a substep
            let targetSection = null;
            let targetStep = null;
            let targetSubstep = null;
            for (const section of targetAllNodes) {
              for (const step of section.steps || []) {
                targetSubstep = (step.substeps || []).find(ss => ss.tree_node_id === pastePosition.targetId);
                if (targetSubstep) {
                  targetSection = section;
                  targetStep = step;
                  break;
                }
              }
              if (targetSubstep) break;
            }
            
            const targetSectionName = targetSection 
              ? (targetIndexMap[targetSection.tree_node_id] || 
                 targetIndexMap[targetSection.section_id_ref] || 
                 `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === targetSection.tree_node_id) + 1)}`)
              : 'the target section';
            
            const targetStepName = targetStep 
              ? (targetIndexMap[targetStep.tree_node_id] || 
                 targetIndexMap[targetStep.step_id_ref] || 
                 `Step ${pastePosition.globalStepNumber || ''}`)
              : 'the target step';
            
            const targetSubstepName = targetSubstep 
              ? (targetIndexMap[targetSubstep.tree_node_id] || 
                 targetIndexMap[targetSubstep.substep_id_ref] || 
                 `Substep in ${targetStepName}`)
              : 'the target substep';
            
            const sourceContext = sourceSubstepName 
              ? `from ${sourceSubstepName} in ${sourceStepName} in ${sourceSectionName}`
              : `from ${sourceStepName} in ${sourceSectionName}`;
            
            actionToShow = `User Copied ${childTypeLabel} ${sourceChildNumber} ${sourceContext} ${sourceDocInfo} into ${targetSubstepName} in ${targetStepName} in ${targetSectionName} ${targetDocInfo}`;
          } else if ((pastePosition.position === 'above' || pastePosition.position === 'below') && pastePosition.targetType === 'child') {
            // Copy above/below another child
            let parent = null;
            let targetChild = null;
            outer: for (const section of targetAllNodes) {
              for (const step of section.steps || []) {
                const inStep = (step.children || []).find(c => {
                  const nodeId = c.tree_node_id;
                  const canvasNodeId = c.detail?.canvas_tree_node_id;
                  return nodeId === pastePosition.targetId || canvasNodeId === pastePosition.targetId;
                });
                if (inStep) {
                  parent = { type: 'step', section, step };
                  targetChild = inStep;
                  break outer;
                }
                for (const substep of step.substeps || []) {
                  const inSub = (substep.children || []).find(c => {
                    const nodeId = c.tree_node_id;
                    const canvasNodeId = c.detail?.canvas_tree_node_id;
                    return nodeId === pastePosition.targetId || canvasNodeId === pastePosition.targetId;
                  });
                  if (inSub) {
                    parent = { type: 'substep', section, step, substep };
                    targetChild = inSub;
                    break outer;
                  }
                }
              }
              if (targetChild) break;
            }
            
            if (parent && targetChild) {
              const targetSectionName = parent.section 
                ? (targetIndexMap[parent.section.tree_node_id] || 
                   targetIndexMap[parent.section.section_id_ref] || 
                   `Section ${(targetAllNodes.findIndex(s => s.tree_node_id === parent.section.tree_node_id) + 1)}`)
                : 'the target section';
              
              const targetStepName = parent.step 
                ? (targetIndexMap[parent.step.tree_node_id] || 
                   targetIndexMap[parent.step.step_id_ref] || 
                   `Step ${pastePosition.globalStepNumber || ''}`)
                : 'the target step';
              
              let targetSubstepName = '';
              if (parent.type === 'substep' && parent.substep) {
                targetSubstepName = targetIndexMap[parent.substep.tree_node_id] || 
                  targetIndexMap[parent.substep.substep_id_ref] || 
                  `Substep in ${targetStepName}`;
              }
              
              const targetChildTypeLabel = formatChildTypeLabel(targetChild.type === 'canvas' ? 'canva' : targetChild.type);
              const targetChildNumber = getChildNodeDisplayNumber(targetChild, targetChild.type === 'canvas' ? 'canva' : targetChild.type, targetAllNodes);
              
              const sourceContext = sourceSubstepName 
                ? `from ${sourceSubstepName} in ${sourceStepName} in ${sourceSectionName}`
                : `from ${sourceStepName} in ${sourceSectionName}`;
              
              const positionWord = pastePosition.position === 'above' ? 'above' : 'below';
              const targetContext = targetSubstepName 
                ? `${positionWord} ${targetChildTypeLabel} ${targetChildNumber} in ${targetSubstepName} in ${targetStepName} in ${targetSectionName}`
                : `${positionWord} ${targetChildTypeLabel} ${targetChildNumber} in ${targetStepName} in ${targetSectionName}`;
              
              actionToShow = `User Copied ${childTypeLabel} ${sourceChildNumber} ${sourceContext} ${sourceDocInfo} ${targetContext} ${targetDocInfo}`;
            } else {
              // Fallback if target child not found
              actionToShow = `User Copied ${childTypeLabel} ${sourceChildNumber} from ${sourceStepName} in ${sourceSectionName} ${sourceDocInfo} ${targetDocInfo}`;
            }
          }
          
          // When copying a child node, ensure the previewed structure (with the inserted child)
          // is captured in the audit trail newContent. Fall back to the existing target data if
          // for some reason the preview data (newData) is unavailable.
          const childNewContent = Array.isArray(newData) && newData.length > 0
            ? newData
            : targetAllNodes;
          newContentForVersion = childNewContent;
          }
        } else {
          // For other node types, use default action_to_show
          newContentForVersion = targetAllNodes;
          actionToShow = `User Copied ${sourceDocument.type} ${sourceDocument.partNumber} Rev ${sourceDocument.revision} into ${targetDocument.type} ${targetDocument.revision} ${targetDocument.partNumber}`;
        }

        // Build version_content with node_indexing if available
        const orgContent = structuredClone(targetAllNodes);
        const newContent = newContentForVersion ? structuredClone(newContentForVersion) : [];
        const versionContent = {
          action_to_show: actionToShow,
            rev: targetDocument.revision,
            status: targetDocument.status || 'Not Submitted',
            item_code_type: targetDocument.type,
            partNumber: targetDocument.partNumber,
            from_part_ref: sourceDocument.partNumber,
            from_part_rev: sourceDocument.revision,
          to_part_ref: targetDocument.partNumber,
          to_part_rev: targetDocument.revision,
          partDescription: targetDocument.description || '',
          main_entry: true,
        };


        // Build audit trail data in old format
        const auditData = await getAuditTrailData({
          action: `${sourceNode?.nodeType?.toUpperCase()}_COPIED`,
          comment: comment || commentText,
          version_content: versionContent,
        },false);

        // Build payload in old format
        const payload = {
          from_procedure_tree_node_ref: fromTreeId,
          from_order: fromOrder,
          from_type: fromType,
          from_part_id_ref: sourceDocument.partId,
          from_procedure_rev_ref: sourceDocument.revisionId,
          from_section_id_ref: fromSectionId,
          from_step_id_ref: fromStepId,
          from_substep_id_ref: fromSubstepId,
          ...(fromChildNodeRef ? { from_child_node_ref: fromChildNodeRef } : {}),
          to_procedure_tree_node_ref: toProcedureTreeNodeRef,
          to_order: parseInt(toOrder || 0, 10),
          to_type: (() => {
            // When pasting above/below a child, use the target child's type
            if (toChildType) {
              return toChildType;
            }
            // Determine to_type based on source node type
            if (sourceNode.nodeType === 'section') return "section";
            if (sourceNode.nodeType === 'step') return "step";
            if (sourceNode.nodeType === 'substep') return "substep";
            // For child nodes (attachment, video, rich-text, form, canvas), use the source node type
            if (['attachment', 'video', 'rich-text', 'form', 'canva', 'canvas'].includes(sourceNode.nodeType)) {
              return sourceNode.nodeType === 'canvas' ? 'canva' : sourceNode.nodeType;
            }
            return "substep"; // final fallback
          })(),
          to_part_id_ref: targetDocument.partId,
          to_procedure_rev_ref: targetDocument.revisionId,
          to_section_id_ref: toSectionIdRef || "",
          to_step_id_ref: toStepIdRef || "",
          to_substep_id_ref: toSubstepIdRef || "",
          direction: direction==="up" ? "paste_up" : direction==="down" ? "paste_down" : direction,
          audit_trail_data: auditData,
        };
        
        const current_snapshot = await uploadJsonToS3(prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDocument.partNumber,
      procedure_rev: targetDocument.revision,
      procedure_status: targetDocument.status || 'Not Submitted',
      item_code_type: targetDocument.type,
      partDescription: targetDocument.description || "",
      part_ref: targetDocument.partId,
      procedure_rev_ref: targetDocument.revisionId
    }),targetDocument.partNumber,targetDocument.revision)

        const current_updated_snapshot = await await uploadJsonToS3(prepareCurrentSnapshot({
      allNodesData: newContent,
      part_number: targetDocument.partNumber,
      procedure_rev: targetDocument.revision,
      procedure_status: targetDocument.status || 'Not Submitted',
      item_code_type: targetDocument.type,
      partDescription: targetDocument.description || "",
      part_ref: targetDocument.partId,
      procedure_rev_ref: targetDocument.revisionId
    }),targetDocument.partNumber,targetDocument.revision+"_current_updated_snapshot",true)

        payload.current_snapshot = current_snapshot
        payload.current_updated_snapshot = current_updated_snapshot

        // Add node_indexing to the main payload if available
        if(childNodeKeys.includes(sourceNode.nodeType)) {
          const previous_content_snapshot = await uploadJsonToS3(orgContent,targetDocument.partNumber,targetDocument.revision+`${sourceNode.nodeType.toUpperCase()}_COPIED_previous`,true)
          const revAndTrainingData = await getRevAndTraining(dispatch, payload.from_part_id_ref)
          const new_content_snapshot = await uploadJsonToS3({
            data:newContent,
            ...payload.audit_trail_data.version_content,
            main_entry:undefined,
            operation:"single_audit_trail",
            tree_node_id:fromTreeId,
            revision:payload.audit_trail_data.version_content.rev,
            ...revAndTrainingData,
          },targetDocument.partNumber,targetDocument.revision+`${sourceNode.nodeType.toUpperCase()}_COPIED_new`,true)
          payload.audit_trail_data.version_content = {
            action_to_show:payload.audit_trail_data.version_content.action_to_show,
          }
          payload.node_indexing = {
            [fromTreeId]: {
              title: actionToShow,
              previous_content_snapshot,
              new_content_snapshot,
            }
          };

          payload.audit_trail_data.previous_content_snapshot = previous_content_snapshot
          payload.audit_trail_data.new_content_snapshot = new_content_snapshot
        }else if (nodeIndexing && Object.keys(nodeIndexing).length > 0) {
          payload.node_indexing = {}
          const revAndTrainingData = await getRevAndTraining(dispatch, payload.from_part_id_ref)
          const snapshot_url = await uploadJsonToS3({
            ...nodeIndexing,
            version_content:{
              ...payload.audit_trail_data.version_content,
              main_entry:undefined,
              revision:payload.audit_trail_data.version_content.rev,
              operation:"bulk_audit_trail",
              ...revAndTrainingData,
            }
          },targetDocument.partNumber,targetDocument.revision+`${sourceNode.nodeType.toUpperCase()}_COPIED`,true)
          
          payload.audit_trail_data.version_content = {
            action_to_show:payload.audit_trail_data.version_content.action_to_show,
            main_entry:true,
            preview_tree_node_id:sourceNode.nodeId
          }

          payload.audit_trail_data.previous_content_snapshot = snapshot_url
          payload.audit_trail_data.new_content_snapshot = snapshot_url

          Object.entries(nodeIndexing).forEach(([key, value]) => {
            payload.node_indexing[key]={
              title:value.title,
              previous_content_snapshot:snapshot_url,
              new_content_snapshot:snapshot_url,
            }
          });
        }

        // Clean up empty or null values (matching old implementation pattern)
        // Note: Empty strings for from_step_id_ref, from_substep_id_ref, to_step_id_ref, to_substep_id_ref are kept
        // Only remove null or empty to_section_id_ref for "into" case
        Object.keys(payload).forEach((key) => {
          if (payload[key] === "null" || payload[key] === null) {
            // Remove null values
            delete payload[key];
          } else if (payload[key] === "") {
            // For "into" case, remove empty to_section_id_ref
            // Keep empty strings for step/substep refs (as per old implementation)
            if (key === 'to_section_id_ref' && pastePosition.position === 'into') {
              delete payload[key];
            }
            // Otherwise keep empty strings (for step/substep refs)
          }
        });

        // Make API call to copy section

        try {
          let response;
          
          // If coming from MovePreviewLayout, use version history endpoints
          if (isFromMovePreview) {
            // Determine which API to use based on whether the node has children
            const childNodeKeys = [ 'attachment','video','rich-text','form','canva','canvas' ];
            let useSimpleNodeCopyAPI = false;
            let sourceNodeData = null;
            
            if (childNodeKeys.includes(sourceNode.nodeType)) {
              // Child nodes always use the simple API
              useSimpleNodeCopyAPI = true;
            } else if (sourceNode.nodeType === 'section') {
              // Check if section has steps
              const hasSteps = sourceSection && sourceSection.steps && sourceSection.steps.length > 0;
              useSimpleNodeCopyAPI = !hasSteps;
              sourceNodeData = sourceSection;
            } else if (sourceNode.nodeType === 'step') {
              // Check if step has substeps or child nodes
              const hasSubsteps = sourceStep && sourceStep.substeps && sourceStep.substeps.length > 0;
              const hasChildren = sourceStep && sourceStep.children && sourceStep.children.length > 0;
              useSimpleNodeCopyAPI = !hasSubsteps && !hasChildren;
              sourceNodeData = sourceStep;
            } else if (sourceNode.nodeType === 'substep') {
              // Check if substep has child nodes
              const hasChildren = sourceSubstep && sourceSubstep.children && sourceSubstep.children.length > 0;
              useSimpleNodeCopyAPI = !hasChildren;
              sourceNodeData = sourceSubstep;
            }
            
            if (useSimpleNodeCopyAPI) {
              // Use the simple node copy API (version-node-copy/create)
              // Build payload for simple node copy API
              const simplePayload = {
                type: payload.to_type, // section, step, substep, canva, rich_text, attachment, video
                sibling_order:
                  sourceNode.nodeType === 'section' ||
                  sourceNode.nodeType === 'step' ||
                  sourceNode.nodeType === 'substep'
                    ? parseInt(String(payload.to_order ?? 0), 10)
                    : String(payload.to_order ?? ''),
                revision_id_ref: targetDocument.revisionId,
                audit_trail_data: payload.audit_trail_data,
              };
              
              // Add optional fields only if they have values
              if (payload.to_section_id_ref) {
                simplePayload.section_id_ref = payload.to_section_id_ref;
              }
              if (payload.to_step_id_ref) {
                simplePayload.step_id_ref = payload.to_step_id_ref;
              }
              if (payload.to_substep_id_ref) {
                simplePayload.substep_id_ref = payload.to_substep_id_ref;
              }
              
              // Add the source node's data to the payload
              if (sourceNodeData) {
                // For section, step, substep - copy their detail fields
                if (sourceNode.nodeType === 'section') {
                  const secDetail =
                    sourceNodeData.detail && typeof sourceNodeData.detail === 'object'
                      ? sourceNodeData.detail
                      : {};
                  const sectionName =
                    sourceNodeData.section_name ?? secDetail.section_name;
                  if (sectionName) {
                    simplePayload.section_name = sectionName;
                  }
                  simplePayload.short_description =
                    sourceNodeData.short_description ??
                    secDetail.short_description ??
                    '';
                  simplePayload.part_required_id_ref = targetDocument.partId;
                  simplePayload.is_hide_heading_and_desc =
                    sourceNodeData.is_hide_heading_and_desc ??
                    secDetail.is_hide_heading_and_desc ??
                    false;
                } else if (sourceNode.nodeType === 'step') {
                  // Copy step data
                  if (sourceNodeData.step_data) {
                    simplePayload.step_data = sourceNodeData.step_data;
                  }
                  // Always include short_description, even if empty
                  simplePayload.short_description = sourceNodeData.short_description || "";
                  simplePayload.is_hide_step_heading = sourceNodeData.is_hide_step_heading || false;
                  simplePayload.is_hide_step_details = sourceNodeData.is_hide_step_details || false;
                  simplePayload.is_hide_bom_table = sourceNodeData.is_hide_bom_table || false;
                  if (sourceNodeData.bom_table_for_step_title_ref) {
                    simplePayload.bom_table_for_step_title_ref = sourceNodeData.bom_table_for_step_title_ref;
                  }
                  if (sourceNodeData.part_number_column_title_ref) {
                    simplePayload.part_number_column_title_ref = sourceNodeData.part_number_column_title_ref;
                  }
                } else if (sourceNode.nodeType === 'substep') {
                  if (sourceNodeData.description) {
                    simplePayload.description = sourceNodeData.description;
                  }
                  // Always include short_description, even if empty
                  simplePayload.short_description = sourceNodeData.short_description || "";
                }
              } else if (childNodeKeys.includes(sourceNode.nodeType)) {
                // For child nodes, we need to get the actual child data
                // Find the child in the source
                let childData = null;
                if (sourceStep) {
                  const children = sourceStep.children || [];
                  childData = children.find(c => {
                    const nodeId = c.tree_node_id;
                    const canvasNodeId = c.detail?.canvas_tree_node_id;
                    return nodeId === sourceNode.nodeId || canvasNodeId === sourceNode.nodeId;
                  });
                } else if (sourceSubstep) {
                  const children = sourceSubstep.children || [];
                  childData = children.find(c => {
                    const nodeId = c.tree_node_id;
                    const canvasNodeId = c.detail?.canvas_tree_node_id;
                    return nodeId === sourceNode.nodeId || canvasNodeId === sourceNode.nodeId;
                  });
                }
                
                if (childData && childData.detail) {
                  // Copy the child node's detail data, but filter out empty strings
                  Object.keys(childData.detail).forEach(key => {
                    const value = childData.detail[key];
                    if (value !== "" && value !== null && value !== undefined) {
                      simplePayload[key] = value;
                    }
                  });
                }
              }
              
              // Remove any empty string values from the payload, except short_description
              Object.keys(simplePayload).forEach(key => {
                if (simplePayload[key] === "" && key !== "short_description") {
                  delete simplePayload[key];
                }
              });
              
              response = await dispatch(copyNodeVersionHistory(simplePayload)).unwrap();
            } else {
              // Use the existing tree copy API (version-copy-procedure-tree-node)
              response = await dispatch(copyVersionProcedureTreeNode(payload)).unwrap();
            }
          } else {
            // return
            // Use regular copy endpoint
            response = await dispatch(copyElement(payload)).unwrap();
          }
         
          
          
          // Show success message based on node type
          const getNodeTypeDisplayName = (nodeType) => {
            const typeMap = {
              'attachment': 'Attachment',
              'video': 'Video',
              'rich-text': 'Rich Text',
              'form': 'Form',
              'canva': 'Canvas',
              'canvas': 'Canvas',
            };
            return typeMap[nodeType] || 'Element';
          };
          
          const successMessage = sourceNode.nodeType === 'step' 
            ? 'Step copied successfully!' 
            : sourceNode.nodeType === 'substep' 
            ? 'Substep copied successfully!'
            : [ 'attachment','video','rich-text','form','canva','canvas' ].includes(sourceNode.nodeType)
            ? `${getNodeTypeDisplayName(sourceNode.nodeType)} copied successfully!`
            : 'Section copied successfully!';
          dispatch(showSnackbar({ message: successMessage, type: 'success' }));
          
          // Navigate to revision-submission page for target document
          const description = targetDocument.description || 'no-description';

          const redirected = await handleRedirectToCopiedNode(
            response,
            targetDocument,
            dispatch,
            navigate,
            getRevAllNodes,
          );
          
          // Close modal and copy replace flow
          setModalOpen(false);
          handleClose();
          // Scroll to top when navigating to revision-submission page
          window.scrollTo(0, 0);
        } catch (error) {
          console.error('Error copying node:', error);
          const errorMessage = sourceNode.nodeType === 'step' 
            ? 'Error copying step: ' + (error?.message || 'Unknown error')
            : sourceNode.nodeType === 'substep'
            ? 'Error copying substep: ' + (error?.message || 'Unknown error')
            : 'Error copying section: ' + (error?.message || 'Unknown error');
          dispatch(showSnackbar({ message: errorMessage, type: 'error' }));
        } finally {
          dispatch(setLoading(false));
        }
      }

      // Document copy flow (existing implementation)
      if (copyMode !== 'document') {
        // Safety guard: do not run document copy when not in document mode
        dispatch(setLoading(false));
        return;
      }
      // Use already fetched data if available, otherwise fetch
      const sourceResponse = newData || await dispatch(getRevAllNodes({ 
        partId: sourceDocument.partId, 
        revisionId: sourceDocument.revisionId 
      })).unwrap();

      const targetResponse = originalData || await dispatch(getRevAllNodes({ 
        partId: targetDocument.partId, 
        revisionId: targetDocument.revisionId 
      })).unwrap();

      const audit_trail_data = await getAuditTrailData({
        action: "REVISION_COPIED",
        comment: comment,
        version_content: {
          action_to_show: `User copied and replaced ${sourceDocument.type} ${sourceDocument.partNumber}, Rev ${sourceDocument.revision} into ${targetDocument.type} ${targetDocument.partNumber}, Rev ${targetDocument.revision}`,
        },
      },false);

      // Build payload for copy operation
      const nodeIndexingResult = buildCopyReplaceNodeIndexing({
        sourceNodes: sourceResponse,
        targetNodes: targetResponse,
        sourceDocument,
        targetDocument,
        startswith:1,
        actionToShow: audit_trail_data.version_content.action_to_show+"."
      });

      const node_indexing = {
        first_entry:{
          title:audit_trail_data.version_content.action_to_show+`. System Performs Operation 1 of ${Object.keys(nodeIndexingResult).length+1}, Copied and replaced ${sourceDocument.type} ${sourceDocument.partNumber} Rev ${sourceDocument.revision}.`,
          originalContent:targetResponse,
          newContent:[],
          first_entry:true
        },
        ...nodeIndexingResult,
      }

      if(sourceResponse.length > 0){
        node_indexing[sourceResponse[0]?.tree_node_id].originalContent = []
      }

      const revAndTrainingData = await getRevAndTraining(dispatch, targetDocument.partId)

      const snapshot_url = await uploadJsonToS3({
        ...node_indexing,
        version_content:{
          action_to_show: audit_trail_data.version_content.action_to_show,
          item_code_type: targetDocument.type,
          partNumber: targetDocument.partNumber,
          status: targetDocument.status || 'Not Submitted',
          revision: targetDocument.revision,
          partDescription:targetDocument.description,
          operation: "bulk_audit_trail",
          ...revAndTrainingData,
          part_ref:targetDocument.partId,
          procedure_rev_ref:targetDocument.revisionId,
          from_item_code_type:sourceDocument.type, 
          from_partNumber:sourceDocument.partNumber, 
          from_revision:sourceDocument.revision, 
        }
      },targetDocument.partNumber,targetDocument.revision+"_COPEID_REPLACE",true)
      // const current_snapshot = await uploadJsonToS3(
      //   prepareCurrentSnapshot(
      //     sourceResponse,
      //     targetDocument.partNumber,
      //     targetDocument.revision,
      //     targetDocument.status || 'Not Submitted',
      //     targetDocument.type
      //   ),
      //   targetDocument.partNumber,targetDocument.revision
      // );

      // const current_updated_snapshot = await uploadJsonToS3(
      //   prepareCurrentSnapshot(
      //     sourceResponse,
      //     targetDocument.partNumber,
      //     targetDocument.revision,
      //     targetDocument.status || 'Not Submitted',
      //     targetDocument.type
      //   ),
      //   targetDocument.partNumber,targetDocument.revision+"_current_updated_snapshot"
      // );

      Object.entries(node_indexing).forEach(([key, value]) => {
        node_indexing[key] = {
          title:node_indexing[key].title,
          previous_content_snapshot:snapshot_url,
          new_content_snapshot:snapshot_url
        }
      })
      
      const payload = {
        part_id_ref: sourceDocument.partId,
        procedure_rev_ref: sourceDocument.revisionId,
        new_part_id: targetDocument.partId,
        new_procedure_id: targetDocument.revisionId,
        tree_node_id:targetDocument.tree_node_id,
        based_on_version_ref: sourceDocument.version_id_ref,
        parent_based_version: targetDocument.parent_based_version,
        audit_trail_data,
        node_indexing
      };

      payload.current_snapshot = null
      payload.current_updated_snapshot = null
      payload.audit_trail_data.previous_content_snapshot = snapshot_url
      payload.audit_trail_data.new_content_snapshot = snapshot_url
      payload.audit_trail_data.version_content = {
        action_to_show: audit_trail_data.version_content.action_to_show,
        main_entry: true
      }
      

      const result = await dispatch(copyProcedure(payload)).unwrap();

      await dispatch(updateCurrentSnapshot({
        partId: targetDocument.partId,
        revisionId: targetDocument.revisionId,
        partNumber: targetDocument.partNumber,
        rev: targetDocument.revision,
        currentStatus: targetDocument.status,
        type: targetDocument.type,
        revAndTrainingData
      }))
      
      // Close modal and copy replace flow
      setModalOpen(false);
      handleClose();
      
      // Show success message using snackbar
      dispatch(showSnackbar({ message: 'Copy & Replace operation completed successfully!', type: 'success' }));
      
      // Navigate to revision-submission page for target document
      const description = targetDocument.description || 'no-description';
      const encodedDescription = encodeURIComponent(description);

      const tasksResponse = await dispatch(fetchtAllTasks({
        searchQuery:`limit=10&offset=0&procedure_ltree_ref=${payload.part_id_ref?.replaceAll("-","_")}.${payload.procedure_rev_ref?.replaceAll("-","_")}`
      })).unwrap();
      navigate(
        `/revision-submission/${targetDocument.partId}/${targetDocument.revision}/${targetDocument.type}/${targetDocument.partNumber}/${targetDocument.revisionId}/${encodedDescription}`,
        { state: { status: 'Not Submitted' } }
      );
      if ((tasksResponse?.results)?.filter(t=>t.status !== "CANCELLED" && t.status !== "COMPLETED")?.length > 0) { 
        // Set copied and redirected flag in Redux
      dispatch(setCopiedAndRedirect(true));
    
      // Open task popup to show the notification
      dispatch(openTaskPopup());
      }
      // Scroll to top when navigating to revision-submission page
      window.scrollTo(0, 0);
      
    } catch (error) {
      console.error('Error copying procedure:', error);
      dispatch(showSnackbar({ message: 'Error copying Document: ' + (error?.message || 'Unknown error'), type: 'error' }));
      dispatch(setLoading(false));
    }
  };

  // Show loader during initial data fetch or when processing paste position
  if (isLoading && (step === 'select' || step === 'pastePosition')) {
    return <Loader text={step === 'select' ? "Loading document data..." : "Loading preview..."} />;
  }

  // Get step title and buttons based on current step
  const getStepConfig = () => {
    // Generate title based on copy mode
    const getSelectTitle = () => {
      if (copyMode === 'node' && sourceNode) {
        // Node copy: matches select-procedure AppBar when copying steps/sections from document
        const srcPart = sourceDocument?.partNumber ?? ''
        const srcRev = sourceDocument?.revision ?? ''
        return `Copying ${sourceNode.nodeTitle} from ${srcPart} ${srcRev}. Select the Procedure`;
      } else {
        // Document copy: "Copy & Replacing {type} from {partNumber} {revision}. Select the Document"
        return `Copy & Replacing ${sourceDocument.type} ${sourceDocument.partNumber}, Rev ${sourceDocument.revision}. Select the Document`;
      }
    };

    switch (step) {
      case 'select':
        return {
          title: getSelectTitle(),
          subheading: '',
          buttonsBeforeTitle: [
            { label: 'Cancel', onClick: handleClose }
          ],
          buttons: []
        };
      case 'pastePosition':
        // Title and buttons handled by PastePositionSelector component
        return {
          title: `Copying ${sourceNode?.nodeTitle || ''} from ${sourceDocument?.partNumber ?? ''} ${sourceDocument?.revision ?? ''}. Where you want to paste?`,
          subheading: '',
          buttonsBeforeTitle: [
            { label: 'Cancel', onClick: handleClose }
          ],
          buttons: [] // Buttons will be updated by PastePositionSelector
        };
      case 'preview':
        return {
          title: 'Please Confirm the Changes',
          subheading: '',
          showCancelButton: true,
          onCancel: handleClose,
          showViewToggle: true,
          selectedView: previewMode,
          onViewChange: (mode) => handlePreviewModeChange(mode),
          buttonsBeforeTitle: [],
          buttons: [
            { label: 'Confirm', onClick: () => setModalOpen(true) }
          ]
        };
      case 'confirm':
        return {
          title: 'Copy & Replace - Confirm',
          subheading: `Confirm copy operation for ${targetDocument.partNumber} Rev ${targetDocument.revision}`,
          buttonsBeforeTitle: [
            { label: 'Back', onClick: handleBack },
            { label: 'Cancel', onClick: handleClose }
          ],
          buttons: []
        };
      default:
        return {
          title: 'Copy & Replace',
          subheading: '',
          buttonsBeforeTitle: [],
          buttons: []
        };
    }
  };

  const stepConfig = getStepConfig();

  return (
    <div>
      {/* Only render header if not in pastePosition step (handled by PastePositionSelector) */}
      {step !== 'pastePosition' && (
        <VersionHistoryCopyAppBar 
          title={stepConfig.title}
          subheading={stepConfig.subheading}
          buttons={stepConfig.buttons}
          buttonsBeforeTitle={stepConfig.buttonsBeforeTitle || []}
          showBackButton={false}
          showProfileButton={false}
          showTablesDropdown={false}
          showCancelButton={stepConfig.showCancelButton || false}
          onCancel={stepConfig.onCancel}
          showViewToggle={stepConfig.showViewToggle || false}
          selectedView={stepConfig.selectedView}
          onViewChange={stepConfig.onViewChange}
        />
      )}
      
      {/* Step 1: Select Target Document */}
      {step === 'select' && (
        <div style={{ padding: '20px' }}>
          
          <SelectRevisionTable
            sourcePartNumber={sourceDocument.partNumber}
            onRevisionSelect={handleRevisionSelect}
            fromRevision={sourceDocument.revision}
            procedureRevisionId={sourceDocument.revisionId}
            isPendingTaskCheckRequired={copyMode !== 'node'}
          />
        </div>
      )}

      {/* Step 2: Select Paste Position (only for node copy) */}
      {step === 'pastePosition' && targetDocument && copyMode === 'node' && (
        <PastePositionSelector
          onPositionSelected={handlePositionSelected}
          onCancel={handleClose}
        />
      )}
      
      {/* Step 3: Preview */}
      {step === 'preview' && targetDocument && (
        <CopyReplacePreview
          sourceDocument={sourceDocument}
          sourceNode={sourceNode}
          copyMode={copyMode}
          targetDocument={targetDocument}
          pastePosition={pastePosition}
          previewMode={previewMode}
          originalData={originalData}
          newData={newData}
          onPreviewModeChange={handlePreviewModeChange}
          onConfirm={handleConfirmSave}
          comment={comment}
          onCommentChange={handleCommentChange}
          isLoading={isLoading}
          isModalOpen={isModalOpen}
          onModalClose={() => setModalOpen(false)}
        />
      )}
      
      {/* Step 3: Confirm */}
      {step === 'confirm' && (
        <CopyReplaceConfirm
          sourceDocument={sourceDocument}
          targetDocument={targetDocument}
          comment={comment}
          onCommentChange={handleCommentChange}
          onConfirm={() => {
            // TODO: Implement actual copy operation
            handleClose();
          }}
        />
      )}
    </div>
  );
};

export default CopyReplace;
